import { formatMoney } from '@theme/money-formatting';
import { buildUpsellCartState, getUpsellOfferCartState, normalizeUpsellTags } from '@theme/iicase-upsell-cart';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

function updateVariantOptionLabel(target) {
  const picker = target.closest('variant-picker');
  if (!picker || target.closest('product-card, quick-add-dialog')) return;

  const option = target.closest('.variant-option');
  const valueLabel = option?.querySelector('.variant-option__swatch-value');
  if (!valueLabel) return;

  if (target instanceof HTMLSelectElement) {
    valueLabel.textContent = target.value;
    return;
  }

  if (target instanceof HTMLInputElement && target.checked) {
    valueLabel.textContent = target.value;
  }
}


const productGroupPickerSelector = '[data-iicase-product-group-picker]';
const sizeMatchingPickerSelector = '[data-iicase-size-matching-picker]';
const productPhoneFilterSelector = '[data-iicase-phone-filter]';
const productPhoneFilterOptionSelector = '[data-iicase-phone-filter-option]';
const collectionPhoneFilterMapSelector = '[data-iicase-card-phone-filter-map]';
const collectionPhoneFilterImageWidths = [352, 533, 750, 1000, 1200];
const productPhoneFilterImageWidths = [352, 533, 750, 1000, 1200, 1600, 1800];
const mobileProductMediaScrollTopOffset = 35;
const enableFastProductVariantPreview = false;
const selectedProductPhoneFilterByScope = new Map();
let pendingMobileProductMediaScrollScope = null;
let pendingMobileProductMediaScrollTimer = 0;
let productMediaPreviewCoverTimer = 0;
let pendingProductVariantPreviewImageData = null;
let pendingProductVariantPreviewScope = null;
const loadedIicaseImageDataKeys = new Set();
const loadingIicaseImageDataPromises = new Map();

function iicaseHandleize(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function getUniqueHandles(values) {
  const handles = [];

  values.forEach((value) => {
    const handle = iicaseHandleize(value);
    if (handle && !handles.includes(handle)) handles.push(handle);
  });

  return handles;
}


function getProductGroupData(picker) {
  if (!(picker instanceof Element)) return null;

  if (picker.__iicaseProductGroupData) return picker.__iicaseProductGroupData;

  const script = picker.querySelector('[data-iicase-product-group-data]');
  if (!script?.textContent?.trim()) return null;

  try {
    const data = JSON.parse(script.textContent);
    picker.__iicaseProductGroupData = data;
    return data;
  } catch (error) {
    return null;
  }
}

function getSizeMatchingData(picker) {
  if (!(picker instanceof Element)) return null;

  if (picker.__iicaseSizeMatchingData) return picker.__iicaseSizeMatchingData;

  const script = picker.querySelector('[data-iicase-size-matching-data]');
  if (!script?.textContent?.trim()) return null;

  try {
    const data = JSON.parse(script.textContent);
    picker.__iicaseSizeMatchingData = data;
    return data;
  } catch (error) {
    return null;
  }
}

function loadProductGroupGuideImages(scope) {
  const images = scope.querySelectorAll?.('[data-iicase-product-group-guide-image]') || [];

  images.forEach((image) => {
    if (!(image instanceof HTMLImageElement) || image.dataset.iicaseProductGroupGuideImageLoaded === 'true') return;

    const src = image.getAttribute('data-src');
    const srcset = image.getAttribute('data-srcset');
    if (!src) return;

    if (srcset) image.setAttribute('srcset', srcset);
    image.setAttribute('src', src);
    image.dataset.iicaseProductGroupGuideImageLoaded = 'true';
  });
}

function getProductGroupVariantPicker(picker) {
  const localVariantPicker = picker
    .closest('.product-details')
    ?.querySelector('variant-picker[data-template-product-match="true"], variant-picker');

  if (localVariantPicker instanceof Element) return localVariantPicker;

  return document.querySelector('main[data-template^="product"] variant-picker[data-template-product-match="true"]');
}

function selectNativeSizeOption(picker, sizeOptionName, desiredSize) {
  const variantPicker = getProductGroupVariantPicker(picker);
  if (!(variantPicker instanceof Element)) return false;

  const sizeOptionHandle = iicaseHandleize(sizeOptionName || 'Size');
  const desiredSizeHandle = iicaseHandleize(desiredSize);
  const selects = Array.from(variantPicker.querySelectorAll('select'));

  for (const select of selects) {
    if (!(select instanceof HTMLSelectElement)) continue;

    const optionName = select.selectedOptions[0]?.dataset.optionName || select.name.match(/^options\[(.+)\]$/)?.[1];
    if (iicaseHandleize(optionName) !== sizeOptionHandle) continue;

    const matchingOption = Array.from(select.options).find(
      (option) => iicaseHandleize(option.value) === desiredSizeHandle
    );
    if (!matchingOption) return false;

    select.value = matchingOption.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  const matchingInput = Array.from(variantPicker.querySelectorAll('fieldset input')).find(
    (input) =>
      input instanceof HTMLInputElement &&
      iicaseHandleize(input.dataset.optionName) === sizeOptionHandle &&
      iicaseHandleize(input.value) === desiredSizeHandle
  );

  if (!(matchingInput instanceof HTMLInputElement)) return false;

  matchingInput.checked = true;
  matchingInput.dispatchEvent(new Event('change', { bubbles: true }));
  return true;
}

function navigateSizeMatchingPicker(picker, selectedOption) {
  const data = getSizeMatchingData(picker);
  if (!data || !(selectedOption instanceof HTMLOptionElement)) return;

  const targetProductId = String(selectedOption.dataset.iicaseSizeTargetProductId || '');
  const currentProductId = String(data.currentProductId || '');
  const oldProductId = String(data.oldProductId || '');
  const desiredSize = selectedOption.value;

  picker.querySelector('[data-iicase-size-matching-label]')?.replaceChildren(desiredSize);

  if (targetProductId === currentProductId) {
    if (targetProductId === oldProductId) {
      selectNativeSizeOption(picker, data.sizeOptionName, desiredSize);
    }
    return;
  }

  const targetProduct = (Array.isArray(data.products) ? data.products : []).find(
    (candidate) => String(candidate.id) === targetProductId
  );
  if (!targetProduct?.url) return;

  const selectedOptions = getSelectedProductVariantOptions(picker).filter(
    (option) => iicaseHandleize(option.name) !== iicaseHandleize(data.sizeOptionName || 'Size')
  );
  if (targetProductId === oldProductId) {
    selectedOptions.push({ name: data.sizeOptionName || 'Size', value: desiredSize });
  }

  window.location.href = buildProductGroupTargetUrl(targetProduct, selectedOptions);
}

function getSelectedProductVariantOptions(picker) {
  const variantPicker = getProductGroupVariantPicker(picker);
  if (!(variantPicker instanceof Element)) return [];

  const selectedOptions = [];

  variantPicker.querySelectorAll('select').forEach((select) => {
    if (!(select instanceof HTMLSelectElement)) return;

    const selectedOption = select.selectedOptions[0];
    const optionName =
      selectedOption?.dataset.optionName || select.name.match(/^options\[(.+)\]$/)?.[1] || select.name;
    const optionValue = selectedOption?.value || select.value;

    if (optionName && optionValue) {
      selectedOptions.push({ name: optionName, value: optionValue });
    }
  });

  variantPicker.querySelectorAll('fieldset input:checked').forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;

    const optionName = input.dataset.optionName;
    const optionValue = input.value;

    if (optionName && optionValue) {
      selectedOptions.push({ name: optionName, value: optionValue });
    }
  });

  return selectedOptions;
}

function findProductGroupTargetProduct(data, desiredSize, desiredCaseType) {
  const products = Array.isArray(data?.products) ? data.products : [];
  if (products.length === 0) return null;

  const desiredSizeHandle = iicaseHandleize(desiredSize);
  const desiredCaseTypeHandle = iicaseHandleize(desiredCaseType);

  return (
    products.find(
      (product) => product.sizeHandle === desiredSizeHandle && product.caseTypeHandle === desiredCaseTypeHandle
    ) ||
    products.find((product) => desiredSizeHandle && product.sizeHandle === desiredSizeHandle) ||
    products.find((product) => desiredCaseTypeHandle && product.caseTypeHandle === desiredCaseTypeHandle) ||
    products[0]
  );
}

function productGroupVariantMatchesOptions(variant, selectedOptions) {
  const variantOptions = Array.isArray(variant?.options) ? variant.options : [];

  return selectedOptions.every((selectedOption) => {
    const matchingOption = variantOptions.find(
      (variantOption) => iicaseHandleize(variantOption.name) === iicaseHandleize(selectedOption.name)
    );

    if (!matchingOption) return true;

    return iicaseHandleize(matchingOption.value) === iicaseHandleize(selectedOption.value);
  });
}

function findProductGroupVariant(product, selectedOptions) {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  if (variants.length === 0 || selectedOptions.length === 0) return null;

  return (
    variants.find((variant) => variant.available && productGroupVariantMatchesOptions(variant, selectedOptions)) ||
    variants.find((variant) => productGroupVariantMatchesOptions(variant, selectedOptions)) ||
    null
  );
}

function buildProductGroupTargetUrl(product, selectedOptions) {
  const url = new URL(product.url, window.location.origin);
  const currentUrl = new URL(window.location.href);
  const matchingVariant = findProductGroupVariant(product, selectedOptions);
  const variantId = matchingVariant?.id || product.defaultVariantId;
  const view = currentUrl.searchParams.get('view');

  if (variantId) url.searchParams.set('variant', variantId);
  if (view) url.searchParams.set('view', view);

  return url.toString();
}

function navigateProductGroupPicker(picker, nextSelection) {
  const data = getProductGroupData(picker);
  if (!data) return;

  const desiredSize = nextSelection.size ?? picker.getAttribute('data-current-size') ?? data.currentSize;
  const desiredCaseType = nextSelection.caseType ?? picker.getAttribute('data-current-case-type') ?? data.currentCaseType;
  const targetProduct = findProductGroupTargetProduct(data, desiredSize, desiredCaseType);
  if (!targetProduct?.url) return;

  const selectedOptions = getSelectedProductVariantOptions(picker);
  window.location.href = buildProductGroupTargetUrl(targetProduct, selectedOptions);
}


function getIicaseImageUrlAtWidth(source, width) {
  if (!source || !width) return source || '';

  try {
    const url = new URL(source, window.location.href);
    url.searchParams.set('width', String(width));
    return url.href;
  } catch (error) {
    return source;
  }
}

function expandIicasePhoneFilterMapEntry(entry, mapScript) {
  if (!entry?.baseSrc) return entry;

  const context = mapScript.dataset.iicasePhoneFilterMapContext || 'card';
  const isProductMap = context === 'product';
  const widths = isProductMap ? productPhoneFilterImageWidths : collectionPhoneFilterImageWidths;
  const defaultWidth = Number(mapScript.dataset.iicasePhoneFilterMapSrcWidth) || (isProductMap ? 1200 : 750);
  const sizes =
    mapScript.dataset.iicasePhoneFilterMapSizes || (isProductMap ? '(min-width: 750px) 50vw, 100vw' : '50vw');

  return {
    ...entry,
    fullSrc: isProductMap ? getIicaseImageUrlAtWidth(entry.baseSrc, 3840) : '',
    sizes,
    src: getIicaseImageUrlAtWidth(entry.baseSrc, defaultWidth),
    srcset: widths.map((width) => `${getIicaseImageUrlAtWidth(entry.baseSrc, width)} ${width}w`).join(', '),
  };
}

function getIicasePhoneFilterMediaMap(root, cacheKey) {
  if (!(root instanceof Element)) return [];

  const mapScript = root.querySelector(collectionPhoneFilterMapSelector);
  const mapSource = mapScript?.textContent?.trim() || '';
  const cachedMap = root[cacheKey];

  if (cachedMap?.source === mapSource && Array.isArray(cachedMap.map)) {
    return cachedMap.map;
  }

  let map = [];

  if (mapSource) {
    try {
      const parsedMap = JSON.parse(mapSource);
      if (Array.isArray(parsedMap)) {
        map = parsedMap
          .map((entry) => expandIicasePhoneFilterMapEntry(entry, mapScript))
          .filter((entry) => entry?.case && entry?.phone && entry?.src);
      }
    } catch (error) {
      map = [];
    }
  }

  Object.defineProperty(root, cacheKey, {
    value: { map, source: mapSource },
    writable: true,
    configurable: true,
  });

  return map;
}

function getProductPhoneFilterMap(root) {
  const scope = getIicaseProductScope(root);
  return getIicasePhoneFilterMediaMap(scope, '__iicaseProductPhoneFilterMap');
}


document.addEventListener('change', (event) => {
  if (!(event.target instanceof HTMLSelectElement || event.target instanceof HTMLInputElement)) return;

  if (event.target instanceof HTMLSelectElement && event.target.matches('[data-iicase-size-matching-select]')) {
    const picker = event.target.closest(sizeMatchingPickerSelector);
    const selectedOption = event.target.selectedOptions[0];
    if (picker && selectedOption) navigateSizeMatchingPicker(picker, selectedOption);
    return;
  }

  if (event.target instanceof HTMLSelectElement && event.target.matches('[data-iicase-product-group-size-select]')) {
    const picker = event.target.closest(productGroupPickerSelector);
    if (picker) navigateProductGroupPicker(picker, { size: event.target.value });
    return;
  }

  updateVariantOptionLabel(event.target);
  previewProductColorSwatchChange(event.target);
}, true);

document.addEventListener(
  'pointerdown',
  (event) => {
    previewProductColorSwatchChange(event.target);
  },
  { capture: true, passive: true }
);

document.addEventListener(
  'click',
  (event) => {
    previewProductColorSwatchChange(event.target);
  },
  { capture: true, passive: true }
);

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const guideTrigger = event.target.closest('[data-iicase-product-group-guide-trigger]');
  if (guideTrigger) {
    const guideDialog = guideTrigger.closest('dialog-component');
    if (guideDialog) loadProductGroupGuideImages(guideDialog);
  }

  const caseTypeOption = event.target.closest('[data-iicase-product-group-case-type]');
  if (!(caseTypeOption instanceof HTMLAnchorElement)) return;

  const picker = caseTypeOption.closest(productGroupPickerSelector);
  if (!picker) return;

  event.preventDefault();
  navigateProductGroupPicker(picker, { caseType: caseTypeOption.getAttribute('data-iicase-product-group-case-type') });
});

const phoneFilterLabelAliases = {
  blue: {
    label: 'Ice Blue',
    swatch: '#dbe9f6',
  },
  'dark-blue': {
    label: 'Deep Blue',
    swatch: '#1f2a4f',
  },
  orange: {
    label: 'Cosmic Orange',
    swatch: '#f79612',
  },
  purple: {
    label: 'Plum',
    swatch: '#6f496f',
  },
};

const preloadedPhoneFilterImages = new Set();
const productPhoneFilterIntroDelay = 2000;
let productPhoneFilterIntroPlayed = false;
let productPhoneFilterIntroDismissed = false;
let productPhoneFilterIntroObserver = null;
let productPhoneFilterIntroStartTimer = 0;

function applyProductPhoneFilterIntroState(root = document) {
  if (!productPhoneFilterIntroDismissed) return;

  root.querySelectorAll?.(productPhoneFilterSelector).forEach((filter) => {
    filter.classList.add('is-intro-dismissed');
  });
}

function dismissProductPhoneFilterIntro() {
  productPhoneFilterIntroDismissed = true;
  applyProductPhoneFilterIntroState();
}

function stopProductPhoneFilterIntro({ markPlayed = true } = {}) {
  if (markPlayed) productPhoneFilterIntroPlayed = true;
  productPhoneFilterIntroObserver?.disconnect();
  productPhoneFilterIntroObserver = null;
  window.clearTimeout(productPhoneFilterIntroStartTimer);
  dismissProductPhoneFilterIntro();
}

function scheduleProductPhoneFilterIntro(filter) {
  if (productPhoneFilterIntroPlayed || !(filter instanceof HTMLElement)) return;

  productPhoneFilterIntroPlayed = true;
  productPhoneFilterIntroObserver?.disconnect();
  productPhoneFilterIntroObserver = null;
  productPhoneFilterIntroStartTimer = window.setTimeout(() => {
    dismissProductPhoneFilterIntro();
  }, productPhoneFilterIntroDelay);
}

function initializeProductPhoneFilterIntro() {
  if (productPhoneFilterIntroPlayed) return;

  const filters = Array.from(document.querySelectorAll(productPhoneFilterSelector));
  if (filters.length === 0) return;

  if (!('IntersectionObserver' in window)) {
    const visibleFilter = filters.find((filter) => filter instanceof HTMLElement && filter.getClientRects().length > 0);
    if (visibleFilter) scheduleProductPhoneFilterIntro(visibleFilter);
    return;
  }

  productPhoneFilterIntroObserver?.disconnect();
  productPhoneFilterIntroObserver = new IntersectionObserver(
    (entries) => {
      const visibleEntry = entries.find(
        (entry) => entry.isIntersecting && entry.target instanceof HTMLElement && entry.target.getClientRects().length > 0
      );
      if (visibleEntry) scheduleProductPhoneFilterIntro(visibleEntry.target);
    },
    { threshold: 0.5 }
  );
  filters.forEach((filter) => productPhoneFilterIntroObserver?.observe(filter));
}

function getProductPhoneFilterScopeKey(element) {
  const fallbackElement =
    element instanceof Element ? element : document.querySelector('main[data-template^="product"]') || document.body;
  const scope = getIicaseProductScope(fallbackElement);
  const scopedElement = scope instanceof Element ? scope : null;
  const productPicker = scope.querySelector?.('variant-picker, swatches-variant-picker-component');

  return (
    scopedElement?.id ||
    scopedElement?.getAttribute('section-id') ||
    scopedElement?.getAttribute('data-section-id') ||
    productPicker?.getAttribute('data-product-id') ||
    productPicker?.getAttribute('data-product-handle') ||
    'main-product'
  );
}

function clearRememberedProductPhoneFilterSelection(gallery) {
  selectedProductPhoneFilterByScope.delete(getProductPhoneFilterScopeKey(gallery));

  const scope = getIicaseProductScope(gallery);
  if (scope instanceof HTMLElement) delete scope.dataset.iicaseSelectedProductPhoneColor;
  if (gallery instanceof HTMLElement) delete gallery.dataset.iicaseSelectedProductPhoneColor;
}

function rememberProductPhoneFilterSelection(gallery, option) {
  const filter = option.closest(productPhoneFilterSelector);
  const firstOption = filter?.querySelector(productPhoneFilterOptionSelector);
  const selectedPhoneColor = option.getAttribute('data-phone-color') || '';
  const initialPhoneColor = gallery.getAttribute('data-iicase-initial-phone-color') || '';

  if ((initialPhoneColor && selectedPhoneColor === initialPhoneColor) || (!initialPhoneColor && option === firstOption)) {
    clearRememberedProductPhoneFilterSelection(gallery);
    return;
  }

  if (!selectedPhoneColor) return;

  const scope = getIicaseProductScope(gallery);
  selectedProductPhoneFilterByScope.set(getProductPhoneFilterScopeKey(gallery), selectedPhoneColor);

  if (scope instanceof HTMLElement) scope.dataset.iicaseSelectedProductPhoneColor = selectedPhoneColor;
  if (gallery instanceof HTMLElement) gallery.dataset.iicaseSelectedProductPhoneColor = selectedPhoneColor;
}

function getRememberedProductPhoneFilterSelection(root) {
  const scope = root instanceof Element ? getIicaseProductScope(root) : document;

  return (
    selectedProductPhoneFilterByScope.get(getProductPhoneFilterScopeKey(root)) ||
    (scope instanceof HTMLElement ? scope.dataset.iicaseSelectedProductPhoneColor : '') ||
    ''
  );
}

function getProductPhoneFilterOptionByColor(root, phoneColor) {
  if (!phoneColor) return null;

  return (
    Array.from(root.querySelectorAll?.(productPhoneFilterOptionSelector) || []).find((option) => {
      return option instanceof HTMLElement && option.getAttribute('data-phone-color') === phoneColor;
    }) || null
  );
}

function getProductPhoneFilterGalleries(root) {
  if (root instanceof Element && root.matches('media-gallery')) return [root];

  return Array.from(root.querySelectorAll?.('media-gallery') || []);
}

function restoreRememberedProductPhoneFilter(root = document, preferredPhoneColor = '') {
  const selectedPhoneColor = preferredPhoneColor || getRememberedProductPhoneFilterSelection(root);
  if (!selectedPhoneColor) return;

  getProductPhoneFilterGalleries(root).forEach((gallery) => {
    const option = getProductPhoneFilterOptionByColor(gallery, selectedPhoneColor);
    if (option instanceof HTMLElement) selectPhoneFilterOption(option, { rememberSelection: false });
  });
}

function scheduleProductPhoneFilterRestore(root = document, preferredPhoneColor = '', delay = 0) {
  const selectedPhoneColor = preferredPhoneColor || getRememberedProductPhoneFilterSelection(root);
  if (!selectedPhoneColor) return;

  window.setTimeout(() => {
    window.requestAnimationFrame(() => restoreRememberedProductPhoneFilter(root, selectedPhoneColor));
  }, delay);
}

function applyPhoneFilterToProductSelectHtml(result, selectedPhoneColor) {
  const html = result?.detail?.html;
  if (!html?.querySelector) return;

  applyProductPhoneFilterIntroState(html);
  if (!selectedPhoneColor) return;

  restoreRememberedProductPhoneFilter(html, selectedPhoneColor);
}

function getPhoneFilterAlias(option) {
  const phoneColor = option.getAttribute('data-phone-color');
  if (!phoneColor) return null;

  return phoneFilterLabelAliases[phoneColor] || null;
}

function applyPhoneFilterAliases(root = document) {
  root.querySelectorAll?.(productPhoneFilterOptionSelector).forEach((option) => {
    if (option.hasAttribute('data-iicase-phone-filter-locked-label')) return;

    const alias = getPhoneFilterAlias(option);
    if (!alias) return;

    if (alias.label) {
      option.setAttribute('data-phone-color-label', alias.label);

      const label = option.querySelector('[data-iicase-phone-filter-option-label]');
      if (label) label.textContent = alias.label;
    }

    if (alias.swatch) {
      option.style.setProperty('--iicase-phone-filter-swatch', alias.swatch);
    }
  });
}

function closePhoneFilterPanel(filter) {
  const panel = filter.querySelector('[data-iicase-phone-filter-panel]');
  const toggle = filter.querySelector('[data-iicase-phone-filter-toggle]');

  if (panel) panel.hidden = true;
  if (toggle) toggle.setAttribute('aria-expanded', 'false');
}

function closeOtherPhoneFilterPanels(currentFilter) {
  document.querySelectorAll(productPhoneFilterSelector).forEach((filter) => {
    if (filter !== currentFilter) closePhoneFilterPanel(filter);
  });
}

function preloadPhoneFilterOptions(filter) {
  filter.querySelectorAll(productPhoneFilterOptionSelector).forEach((option) => {
    const src = option.getAttribute('data-src');
    if (!src || preloadedPhoneFilterImages.has(src)) return;

    preloadedPhoneFilterImages.add(src);

    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = 'low';
    image.sizes = option.getAttribute('data-sizes') || '100vw';
    image.srcset = option.getAttribute('data-srcset') || '';
    image.src = src;
  });
}

function normalizeIicaseImageUrl(value) {
  if (!value) return '';

  try {
    return new URL(value, window.location.href).href;
  } catch (error) {
    return value;
  }
}

function imageMatchesIicaseImageData(image, imageData) {
  if (!(image instanceof HTMLImageElement) || !imageData?.src) return false;

  const currentSrc = normalizeIicaseImageUrl(image.getAttribute('src') || '');
  const nextSrc = normalizeIicaseImageUrl(imageData.src);
  const currentFullSrc = normalizeIicaseImageUrl(
    image.getAttribute('data-max-resolution') || image.getAttribute('data_max_resolution') || ''
  );
  const nextFullSrc = normalizeIicaseImageUrl(imageData.fullSrc || '');

  return (
    currentSrc === nextSrc &&
    (image.getAttribute('srcset') || '') === (imageData.srcset || '') &&
    (image.getAttribute('sizes') || '') === (imageData.sizes || image.getAttribute('sizes') || '') &&
    (!imageData.alt || (image.getAttribute('alt') || '') === imageData.alt) &&
    (!nextFullSrc || currentFullSrc === nextFullSrc)
  );
}

function getIicaseImageDataKey(imageData) {
  if (!imageData?.src) return '';

  return imageData.key || (imageData.srcset ? `${imageData.srcset}|${imageData.sizes || ''}` : imageData.src);
}

function getIicaseImageDataFromImage(image) {
  if (!(image instanceof HTMLImageElement)) return null;

  const src = image.getAttribute('src') || '';
  const srcset = image.getAttribute('srcset') || '';
  const sizes = image.getAttribute('sizes') || '';

  if (!src) return null;

  return {
    alt: image.getAttribute('alt') || '',
    fullSrc: image.getAttribute('data-max-resolution') || image.getAttribute('data_max_resolution') || '',
    key: srcset ? `${srcset}|${sizes}` : src,
    sizes,
    src,
    srcset,
  };
}

function markIicaseImageDataLoaded(imageData) {
  const key = getIicaseImageDataKey(imageData);
  if (key) loadedIicaseImageDataKeys.add(key);
}

function isIicaseImageDataLoaded(imageData) {
  const key = getIicaseImageDataKey(imageData);
  if (!key) return false;
  if (loadedIicaseImageDataKeys.has(key)) return true;

  const matchingImage = Array.from(document.images).find(
    (image) => image.complete && image.naturalWidth > 0 && imageMatchesIicaseImageData(image, imageData)
  );

  if (matchingImage) {
    markIicaseImageDataLoaded(imageData);
    return true;
  }

  return false;
}

function loadIicaseImageData(imageData, fetchPriority = 'low') {
  const key = getIicaseImageDataKey(imageData);
  if (!key || !imageData?.src) return Promise.resolve(false);
  if (isIicaseImageDataLoaded(imageData)) return Promise.resolve(true);

  const existingPromise = loadingIicaseImageDataPromises.get(key);
  if (existingPromise) return existingPromise;

  const image = new Image();
  image.decoding = 'async';
  image.fetchPriority = fetchPriority;

  const promise = new Promise((resolve) => {
    let settled = false;

    const finish = (isLoaded) => {
      if (settled) return;

      settled = true;
      loadingIicaseImageDataPromises.delete(key);

      if (isLoaded) markIicaseImageDataLoaded(imageData);
      resolve(isLoaded);
    };

    const handleLoad = () => {
      const decodePromise = typeof image.decode === 'function' ? image.decode().catch(() => {}) : Promise.resolve();
      decodePromise.then(() => finish(true));
    };

    image.onload = handleLoad;
    image.onerror = () => finish(false);

    if (imageData.sizes) image.sizes = imageData.sizes;
    if (imageData.srcset) image.srcset = imageData.srcset;
    image.src = imageData.src;

    if (image.complete) {
      if (image.naturalWidth > 0) {
        handleLoad();
      } else {
        finish(false);
      }
    }
  });

  loadingIicaseImageDataPromises.set(key, promise);
  return promise;
}

function applyIicaseImageData(image, imageData) {
  if (!(image instanceof HTMLImageElement) || !imageData?.src) return;
  if (imageMatchesIicaseImageData(image, imageData)) return;

  image.decoding = 'async';
  if (imageData.sizes) image.sizes = imageData.sizes;
  if (imageData.srcset) image.srcset = imageData.srcset;
  image.src = imageData.src;
  if (imageData.alt) image.alt = imageData.alt;

  if (imageData.fullSrc) {
    image.setAttribute('data-max-resolution', imageData.fullSrc);
    image.setAttribute('data_max_resolution', imageData.fullSrc);
  }

  if (image.complete && image.naturalWidth > 0) {
    markIicaseImageDataLoaded(imageData);
  } else {
    image.addEventListener('load', () => markIicaseImageDataLoaded(imageData), { once: true });
  }
}

function togglePhoneFilterPanel(toggle) {
  const filter = toggle.closest(productPhoneFilterSelector);
  const panel = filter?.querySelector('[data-iicase-phone-filter-panel]');
  if (!filter || !panel) return;

  applyPhoneFilterAliases(filter);
  closeOtherPhoneFilterPanels(filter);

  const willOpen = panel.hidden;
  if (willOpen) stopProductPhoneFilterIntro();
  panel.hidden = !willOpen;
  toggle.setAttribute('aria-expanded', String(willOpen));

  if (willOpen) preloadPhoneFilterOptions(filter);
}

function rememberProductPhoneOriginalImage(image) {
  if (!image.dataset.iicaseOriginalSrc) {
    image.dataset.iicaseOriginalSrc = image.getAttribute('src') || '';
    image.dataset.iicaseOriginalSrcset = image.getAttribute('srcset') || '';
    image.dataset.iicaseOriginalSizes = image.getAttribute('sizes') || '';
    image.dataset.iicaseOriginalAlt = image.getAttribute('alt') || '';
    image.dataset.iicaseOriginalMaxResolution =
      image.getAttribute('data-max-resolution') || image.getAttribute('data_max_resolution') || '';
  }
}

function restoreProductPhoneImage(image) {
  if (!(image instanceof HTMLImageElement) || !image.dataset.iicaseOriginalSrc) return;

  const original = {
    alt: image.dataset.iicaseOriginalAlt || '',
    fullSrc: image.dataset.iicaseOriginalMaxResolution || '',
    sizes: image.dataset.iicaseOriginalSizes || '',
    src: image.dataset.iicaseOriginalSrc,
    srcset: image.dataset.iicaseOriginalSrcset || '',
  };

  if (original.sizes) {
    image.setAttribute('sizes', original.sizes);
  } else {
    image.removeAttribute('sizes');
  }

  if (original.srcset) {
    image.setAttribute('srcset', original.srcset);
  } else {
    image.removeAttribute('srcset');
  }

  image.setAttribute('src', original.src);
  image.setAttribute('alt', original.alt);

  if (original.fullSrc) {
    image.setAttribute('data-max-resolution', original.fullSrc);
    image.setAttribute('data_max_resolution', original.fullSrc);
  } else {
    image.removeAttribute('data-max-resolution');
    image.removeAttribute('data_max_resolution');
  }
}

function updatePhoneFilterImageData(image, imageData) {
  if (!(image instanceof HTMLImageElement) || !imageData?.src) return;
  if (imageMatchesIicaseImageData(image, imageData)) return;

  rememberProductPhoneOriginalImage(image);

  applyIicaseImageData(image, imageData);
}

function updatePhoneFilterImage(image, option) {
  const src = option.getAttribute('data-src');
  if (!src) return;

  updatePhoneFilterImageData(image, {
    alt: option.getAttribute('data-alt') || '',
    fullSrc: option.getAttribute('data-full-src') || '',
    sizes: option.getAttribute('data-sizes') || image.sizes,
    src,
    srcset: option.getAttribute('data-srcset') || '',
  });
}

function getIicaseImageSourceKey(value) {
  if (!value) return '';

  try {
    return decodeURIComponent(new URL(value, window.location.href).pathname);
  } catch (error) {
    return String(value).split('?')[0];
  }
}

function clearProductPhoneFilterImageState(image) {
  if (!(image instanceof HTMLImageElement)) return;

  delete image.dataset.iicasePhoneFilterSlot;
  delete image.dataset.iicaseOriginalSrc;
  delete image.dataset.iicaseOriginalSrcset;
  delete image.dataset.iicaseOriginalSizes;
  delete image.dataset.iicaseOriginalAlt;
  delete image.dataset.iicaseOriginalMaxResolution;
}

function identifyProductPhoneFilterSlotImages(gallery, phoneFilterMap) {
  const slotsBySource = new Map();
  const mapSignature = phoneFilterMap
    .map((entry) => `${entry.case}:${entry.phone}:${entry.slot || ''}:${entry.baseSrc || ''}`)
    .join('|');

  if (gallery.__iicasePhoneFilterSlotMapSignature !== mapSignature) {
    gallery.querySelectorAll('img').forEach(clearProductPhoneFilterImageState);
    gallery.__iicasePhoneFilterSlotMapSignature = mapSignature;
  }

  phoneFilterMap.forEach((entry) => {
    const slot = Number(entry.slot);
    if (!slot) return;

    [entry.baseSrc, entry.src, entry.fullSrc].forEach((source) => {
      const sourceKey = getIicaseImageSourceKey(source);
      if (sourceKey) slotsBySource.set(sourceKey, slot);
    });
  });

  gallery.querySelectorAll('img').forEach((image) => {
    if (!(image instanceof HTMLImageElement) || image.dataset.iicasePhoneFilterSlot) return;

    const imageSources = [
      image.currentSrc,
      image.getAttribute('src'),
      image.getAttribute('data-max-resolution'),
      image.getAttribute('data_max_resolution'),
    ];

    const slot = imageSources
      .map((source) => slotsBySource.get(getIicaseImageSourceKey(source)))
      .find((matchedSlot) => Number(matchedSlot) > 0);

    if (slot) image.dataset.iicasePhoneFilterSlot = String(slot);
  });

  return Array.from(gallery.querySelectorAll('img[data-iicase-phone-filter-slot]'));
}

function setProductPhoneFilterManagedHidden(element, shouldHide) {
  if (!(element instanceof HTMLElement)) return;

  if (shouldHide) {
    if (!element.hasAttribute('hidden')) element.dataset.iicasePhoneFilterManagedHidden = 'true';
    element.setAttribute('hidden', '');
    return;
  }

  if (!element.hasAttribute('data-iicase-phone-filter-managed-hidden')) return;

  element.removeAttribute('hidden');
  delete element.dataset.iicasePhoneFilterManagedHidden;
}

function updateProductPhoneFilterCounter(counter, visibleSlideCount) {
  if (!(counter instanceof HTMLElement)) return;

  const totalNode = Array.from(counter.childNodes)
    .reverse()
    .find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());

  if (totalNode) totalNode.textContent = String(visibleSlideCount);
}

function syncProductPhoneFilterGalleryVisibility(gallery, entriesBySlot) {
  const slideshow = gallery.querySelector(':scope > slideshow-component');
  const slides = Array.from(
    slideshow?.querySelectorAll(':scope > slideshow-container > slideshow-slides > slideshow-slide') || []
  );
  if (!slides.length) return;

  slides.forEach((slide) => {
    const slotImage = slide.querySelector('img[data-iicase-phone-filter-slot]');
    if (!(slotImage instanceof HTMLImageElement)) return;

    const slot = Number(slotImage.dataset.iicasePhoneFilterSlot);
    const shouldHide = slot > 0 && !entriesBySlot.has(slot);
    setProductPhoneFilterManagedHidden(slide, shouldHide);
    if (shouldHide) slide.setAttribute('aria-hidden', 'true');
  });

  const visibleSlides = slides.filter((slide) => !slide.hasAttribute('hidden'));
  const visibleIndexBySlide = new Map(visibleSlides.map((slide, index) => [slide, index]));

  slides.forEach((slide) => {
    const clickAction = slide.getAttribute('on:click');
    if (!clickAction || !/\/open\/\d+$/.test(clickAction)) return;

    if (!slide.dataset.iicasePhoneFilterOriginalZoomAction) {
      slide.dataset.iicasePhoneFilterOriginalZoomAction = clickAction;
    }

    const visibleIndex = visibleIndexBySlide.get(slide);
    if (visibleIndex === undefined) return;

    slide.setAttribute(
      'on:click',
      slide.dataset.iicasePhoneFilterOriginalZoomAction.replace(/\/open\/\d+$/, `/open/${visibleIndex}`)
    );
  });

  slideshow?.querySelectorAll('slideshow-controls').forEach((controls) => {
    const controlGroups = [
      {
        buttons: Array.from(controls.querySelectorAll('.slideshow-controls__thumbnail')),
        ref: 'thumbnails[]',
      },
      {
        buttons: Array.from(controls.querySelectorAll('.slideshow-controls__dots .slideshow-control')),
        ref: 'dots[]',
      },
    ];

    controlGroups.forEach(({ buttons, ref }) => {
      buttons.forEach((button, originalIndex) => {
        const slide = slides[originalIndex];
        const visibleIndex = slide ? visibleIndexBySlide.get(slide) : undefined;
        const shouldHide = visibleIndex === undefined;

        setProductPhoneFilterManagedHidden(button, shouldHide);
        if (button.closest('.slideshow-controls__dots')) {
          setProductPhoneFilterManagedHidden(button.closest('li'), shouldHide);
        }

        if (shouldHide) {
          button.removeAttribute('ref');
          button.setAttribute('aria-selected', 'false');
          return;
        }

        button.setAttribute('ref', ref);
        button.setAttribute('on:click', `/select/${visibleIndex}`);
      });
    });

    updateProductPhoneFilterCounter(
      controls.querySelector('.slideshow-controls__counter'),
      visibleSlides.length
    );
  });
}

function applyProductPhoneFilterSlots(gallery, option) {
  const selectedPhoneColor = option.getAttribute('data-phone-color') || '';
  const filter = option.closest(productPhoneFilterSelector);
  const selectedCaseColor = iicaseHandleize(filter?.getAttribute('data-selected-case-color') || '');
  if (!selectedPhoneColor || !selectedCaseColor) return false;

  const phoneFilterMap = getProductPhoneFilterMap(gallery);
  const sharedSlotEntries = phoneFilterMap.filter(
    (entry) => entry.phone === 'all' && entry.case === selectedCaseColor && Number(entry.slot) > 0
  );
  const selectedSlotEntries = phoneFilterMap.filter(
    (entry) => entry.phone === selectedPhoneColor && entry.case === selectedCaseColor && Number(entry.slot) > 0
  );
  const entriesBySlot = new Map();

  sharedSlotEntries.forEach((entry) => entriesBySlot.set(Number(entry.slot), entry));
  selectedSlotEntries.forEach((entry) => entriesBySlot.set(Number(entry.slot), entry));

  const slotEntries = Array.from(entriesBySlot.values()).sort(
    (first, second) => Number(first.slot) - Number(second.slot)
  );

  if (!slotEntries.length) return false;

  const slotImages = identifyProductPhoneFilterSlotImages(gallery, phoneFilterMap);
  if (!slotImages.length) return false;

  syncProductPhoneFilterGalleryVisibility(gallery, entriesBySlot);

  slotImages.forEach((image) => {
    const slot = Number(image.dataset.iicasePhoneFilterSlot);
    if (!slot) return;

    const entry = entriesBySlot.get(slot);
    if (!entry) {
      restoreProductPhoneImage(image);
      return;
    }

    const isPrimaryGalleryImage = slot === 1 && image.classList.contains('product-media__image');
    image.fetchPriority = isPrimaryGalleryImage ? 'high' : 'low';
    if (!isPrimaryGalleryImage) image.loading = 'lazy';

    updatePhoneFilterImageData(image, {
      ...entry,
      alt: entry.alt || image.getAttribute('alt') || '',
      sizes: image.getAttribute('sizes') || entry.sizes,
    });
  });

  return true;
}

function syncPhoneFilterControls(gallery, option) {
  const label = option.getAttribute('data-phone-color-label') || 'Phone Color';
  const swatch = option.style.getPropertyValue('--iicase-phone-filter-swatch');
  const selectedPhoneColor = option.getAttribute('data-phone-color');

  gallery.querySelectorAll(productPhoneFilterSelector).forEach((filter) => {
    const currentLabel = filter.querySelector('[data-iicase-phone-filter-current]');
    if (currentLabel) currentLabel.textContent = label;

    const toggleSwatch = filter.querySelector('[data-iicase-phone-filter-toggle] .iicase-phone-filter__swatch');
    if (toggleSwatch && swatch) toggleSwatch.style.setProperty('--iicase-phone-filter-swatch', swatch);

    filter.querySelectorAll(productPhoneFilterOptionSelector).forEach((otherOption) => {
      otherOption.setAttribute(
        'aria-pressed',
        String(otherOption.getAttribute('data-phone-color') === selectedPhoneColor)
      );
    });

    closePhoneFilterPanel(filter);
  });
}

function selectPhoneFilterOption(option, { rememberSelection = true } = {}) {
  const filter = option.closest(productPhoneFilterSelector);
  const gallery = option.closest('media-gallery');
  if (!filter || !gallery) return;

  applyPhoneFilterAliases(filter);
  if (rememberSelection) rememberProductPhoneFilterSelection(gallery, option);
  if (rememberSelection) scheduleVariantImagePreload(0, { force: true });

  const appliedSlottedImages = applyProductPhoneFilterSlots(gallery, option);

  if (!appliedSlottedImages) {
    const imageSelectors = [
      'slideshow-slides > slideshow-slide:first-of-type img.product-media__image',
      '.media-gallery__grid > li:first-child img.product-media__image',
      '.dialog-zoomed-gallery > li:first-child img.product-media__image',
      '.dialog-thumbnails-list__thumbnail:first-child img',
    ];
    const images = [...new Set(imageSelectors.flatMap((selector) => Array.from(gallery.querySelectorAll(selector))))];

    images.forEach((image) => updatePhoneFilterImage(image, option));
  }

  syncPhoneFilterControls(gallery, option);
  gallery.slideshow?.select(0, undefined, { animate: false });
}

function stopPhoneFilterMediaEvent(event) {
  if (!(event.target instanceof Element)) return;
  if (!event.target.closest(productPhoneFilterSelector)) return;

  event.stopPropagation();
}

function handlePhoneFilterClick(event) {
  if (!(event.target instanceof Element)) return false;

  const filter = event.target.closest(productPhoneFilterSelector);
  if (!filter) return false;

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  const toggle = event.target.closest('[data-iicase-phone-filter-toggle]');
  if (toggle) {
    togglePhoneFilterPanel(toggle);
    return true;
  }

  const option = event.target.closest(productPhoneFilterOptionSelector);
  if (option) {
    selectPhoneFilterOption(option);
    return true;
  }

  return true;
}

document.addEventListener('pointerdown', stopPhoneFilterMediaEvent, true);
document.addEventListener('pointerup', stopPhoneFilterMediaEvent, true);
document.addEventListener('touchstart', stopPhoneFilterMediaEvent, true);
document.addEventListener('touchend', stopPhoneFilterMediaEvent, true);
document.addEventListener('click', handlePhoneFilterClick, true);

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  if (!event.target.closest(productPhoneFilterSelector)) {
    document.querySelectorAll(productPhoneFilterSelector).forEach(closePhoneFilterPanel);
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;

  document.querySelectorAll(productPhoneFilterSelector).forEach(closePhoneFilterPanel);
});


if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      applyPhoneFilterAliases();
      initializeProductPhoneFilterIntro();
    },
    { once: true }
  );
} else {
  applyPhoneFilterAliases();
  initializeProductPhoneFilterIntro();
}

const upsellOfferSelector = '[data-iicase-upsell-offer]';
const upsellItemSelector = '[data-iicase-upsell-item]';
const addToCartTotalSelector = '[data-iicase-add-to-cart-total]';
let upsellCartState = buildUpsellCartState(parseUpsellJson(document, '[data-iicase-upsell-cart-state]') || {});
let upsellCartRevision = 0;
let upsellCartRead = null;
const upsellPendingSelections = new WeakMap();

function parseUpsellJson(root, selector) {
  const script = root.querySelector(selector);
  if (!script?.textContent?.trim()) return null;

  try {
    return JSON.parse(script.textContent);
  } catch (error) {
    return null;
  }
}

function getCurrentUpsellCartState(root) {
  return getUpsellOfferCartState(
    upsellCartState,
    root.getAttribute('data-iicase-upsell-offer-id') || '',
    root.querySelector('[data-iicase-upsell-free-gift]')?.getAttribute('data-gift-variant-id') || '',
    root.getAttribute('data-iicase-upsell-inner-product-tags')
  );
}

function updateUpsellCartSummaries() {
  document.querySelectorAll(upsellOfferSelector).forEach((root) => updateUpsellSummary(root));
}

function readUpsellCartSection(html) {
  if (typeof html !== 'string' || !html.includes('data-iicase-upsell-cart-state')) return null;
  const section = new DOMParser().parseFromString(html, 'text/html');
  const cart = parseUpsellJson(section, '[data-iicase-upsell-cart-state]');
  return cart?.includes_product_tags && Array.isArray(cart.items) ? cart : null;
}

async function reconcileUpsellCartDrawer(cart, revision) {
  const drawer = document.querySelector('cart-drawer-component cart-items-component');
  const sectionId = drawer?.dataset.sectionId;
  if (!sectionId || JSON.stringify(parseUpsellJson(drawer, '[data-iicase-upsell-cart-state]')) === JSON.stringify(cart)) return;

  // Cached history can also restore old drawer rows. Only fetch its HTML when the snapshot differs.
  const html = await sectionRenderer.getSectionHTML(sectionId, false);
  if (revision !== upsellCartRevision) return;
  const refreshedCart = readUpsellCartSection(html);
  if (!refreshedCart) throw new Error('Cart drawer is missing product tags');
  await morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });
  if (revision !== upsellCartRevision) return;
  upsellCartState = buildUpsellCartState(refreshedCart);
  updateUpsellCartSummaries();
  const itemCount = refreshedCart.items.reduce((sum, item) => sum + item.quantity, 0);
  document.querySelectorAll('cart-icon').forEach((icon) => icon.renderCartBubble?.(itemCount, false));
}

function refreshUpsellCartState() {
  if (!document.querySelector(upsellOfferSelector)) return Promise.resolve();
  if (upsellCartRead) return upsellCartRead;
  const revision = upsellCartRevision;
  // One lightweight section supplies quantities and tags; Ajax cart JSON omits product tags.
  upsellCartRead = fetch(`${window.Shopify?.routes?.root || '/'}?section_id=iicase-upsell-cart-state`, { cache: 'no-store' })
    .then((response) => {
      if (!response.ok) throw new Error('Unable to refresh cart');
      return response.text();
    })
    .then(async (html) => {
      if (revision !== upsellCartRevision) return false;
      const cart = readUpsellCartSection(html);
      if (!cart) throw new Error('Cart section is missing product tags');
      upsellCartState = buildUpsellCartState(cart);
      updateUpsellCartSummaries();
      await reconcileUpsellCartDrawer(cart, revision);
      return revision === upsellCartRevision;
    })
    .catch((error) => console.warn('[iicase-upsell] Cart refresh failed:', error))
    .finally(() => { upsellCartRead = null; })
    .then((applied) => applied === false ? refreshUpsellCartState() : undefined);
  return upsellCartRead;
}

function clearSubmittedUpsellSelection(submission) {
  if (!submission) return;
  // A variant refresh can replace the original offer nodes during the request.
  document.querySelectorAll(upsellOfferSelector).forEach((root) => {
    if (root.getAttribute('data-iicase-upsell-offer-id') !== submission.offerId) return;
    root.querySelectorAll(upsellItemSelector).forEach((item) => {
      const select = item.querySelector('[data-iicase-upsell-select]');
      const submitted = submission.items.find((entry) => entry.variantId === select?.value);
      if (!submitted || getUpsellItemQuantity(item) !== submitted.quantity) return;
      const checkbox = item.querySelector('[data-iicase-upsell-toggle]');
      if (checkbox instanceof HTMLInputElement) checkbox.checked = false;
      if (isCompactUpsellItem(item)) setUpsellItemQuantity(item, 0, !select.disabled);
    });
  });
}

async function handleUpsellCartUpdate(event) {
  if (!event.promise || !document.querySelector(upsellOfferSelector)) return;
  const revision = ++upsellCartRevision;
  const submission = event.action === 'add' ? upsellPendingSelections.get(event.target) : null;
  upsellPendingSelections.delete(event.target);
  try {
    const result = await event.promise;
    if (result?.detail?.didError) return;
    clearSubmittedUpsellSelection(submission);
    // Never let a slower, older response replace the latest cart state.
    if (revision !== upsellCartRevision) {
      await refreshUpsellCartState();
      return;
    }
    // Invalidate reads started while this mutation was still in flight.
    ++upsellCartRevision;
    let cart = null;
    for (const html of Object.values(result?.detail?.sections || {})) {
      cart = readUpsellCartSection(html);
      if (cart) break;
    }
    if (cart) {
      upsellCartState = buildUpsellCartState(cart);
      updateUpsellCartSummaries();
    } else {
      await refreshUpsellCartState();
    }
  } catch (error) {
    // Keep selections on failure; reconcile in case an overlapping update succeeded.
    await refreshUpsellCartState();
  }
}

document.addEventListener('shopify:cart:lines-update', handleUpsellCartUpdate);
document.addEventListener('shopify:cart:discount-update', handleUpsellCartUpdate);
// History restores may reuse cached HTML without setting pageshow.persisted.
window.addEventListener('pageshow', refreshUpsellCartState);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') refreshUpsellCartState();
});

function normalizeUpsellValue(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&amp;/g, '&')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function getUpsellMatchNames(root) {
  const raw = root.getAttribute('data-iicase-upsell-match-options') || 'Size, Model, Device';
  return raw
    .split(',')
    .map((name) => normalizeUpsellValue(name))
    .filter(Boolean);
}

function getUpsellVariantSelectionMode(root) {
  const mode = normalizeUpsellValue(root.getAttribute('data-iicase-upsell-selection-mode'));

  return mode === 'manual' || mode === 'manual-select' ? 'manual-select' : 'auto-match';
}

function findUpsellMatchedOptionIndex(productData, matchNames) {
  const optionNames = Array.isArray(productData?.options) ? productData.options : [];

  return optionNames.findIndex((optionName) => {
    const normalizedOptionName = normalizeUpsellValue(optionName);
    if (!normalizedOptionName) return false;

    return matchNames.some((matchName) => {
      return normalizedOptionName === matchName || normalizedOptionName.includes(matchName);
    });
  });
}

function getIicaseProductScope(element) {
  if (!(element instanceof Element)) return document;

  return element.closest('product-info') || element.closest('.shopify-section') || element.closest('main') || document;
}

function isIicaseMobileViewport() {
  return window.matchMedia('(max-width: 749px)').matches;
}

function showProductVariantLoading(event) {
  if (!(event.target instanceof Element) || event.target.closest('product-card, quick-add-dialog')) return null;

  const picker = event.target.matches('variant-picker') ? event.target : event.target.closest('variant-picker');
  if (!(picker instanceof Element) || !picker.closest('main[data-template^="product"]')) return null;

  picker.querySelector('.iicase-variant-loading-indicator')?.remove();
  document.querySelectorAll('body > .iicase-variant-loading-indicator--select').forEach((indicator) => {
    indicator.remove();
  });
  picker.querySelectorAll('[data-iicase-variant-loading]').forEach((option) => {
    option.removeAttribute('data-iicase-variant-loading');
  });

  const optionValueId = String(event.detail?.optionValueId || '');
  const selectedOption = Array.from(picker.querySelectorAll('[data-option-value-id]')).find(
    (option) => option.getAttribute('data-option-value-id') === optionValueId
  );
  const optionGroup = selectedOption?.closest('.variant-option');
  const label = optionGroup?.querySelector(':scope > legend, :scope > label');
  const selectWrapper = selectedOption instanceof HTMLOptionElement
    ? optionGroup?.querySelector('.variant-option__select-wrapper')
    : null;
  if (!(label instanceof HTMLElement) && !(selectWrapper instanceof HTMLElement)) return null;
  const sizeGuideLabelText = label?.querySelector(
    ':scope > .iicase-native-size-guide__label-row > span:first-child'
  );
  const loadingTarget = sizeGuideLabelText instanceof HTMLElement ? sizeGuideLabelText : label;

  const indicator = document.createElement('span');
  indicator.className = 'iicase-variant-loading-indicator';
  indicator.setAttribute('aria-hidden', 'true');
  const startedAt = performance.now();

  if (selectWrapper instanceof HTMLElement) {
    const selectRect = selectWrapper.getBoundingClientRect();
    optionGroup.setAttribute('data-iicase-variant-loading', 'true');
    indicator.classList.add('iicase-variant-loading-indicator--select');
    indicator.style.insetBlockStart = `${selectRect.top + (selectRect.height - 13) / 2}px`;
    indicator.style.insetInlineStart = `${selectRect.right - 28}px`;
    document.body.append(indicator);
  } else {
    loadingTarget.append(indicator);
  }

  return () => {
    const remove = () => {
      if (!indicator.isConnected) return;
      optionGroup.removeAttribute('data-iicase-variant-loading');
      indicator.remove();
    };
    const minimumDuration = selectWrapper instanceof HTMLElement ? 320 : 0;
    window.setTimeout(remove, Math.max(0, minimumDuration - (performance.now() - startedAt)));
  };
}

function getIicaseStickyHeaderOffset() {
  const header = document.querySelector('#header-group, header-component, sticky-header');
  if (!(header instanceof Element)) return 0;

  const rect = header.getBoundingClientRect();
  if (rect.height <= 0 || rect.top > 0 || rect.bottom <= 0) return 0;

  return Math.min(rect.bottom, 96);
}

function scrollMobileProductMediaToTop(scope) {
  if (!isIicaseMobileViewport()) return;

  const productScope = getIicaseProductScope(scope instanceof Element ? scope : document.querySelector('main[data-template^="product"]'));
  const media = getProductInformationMedia(productScope);
  if (!(media instanceof Element)) return;

  const mediaTop = window.scrollY + media.getBoundingClientRect().top;
  const targetTop = Math.max(0, mediaTop - getIicaseStickyHeaderOffset() - mobileProductMediaScrollTopOffset - 6);
  const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

  window.scrollTo({ top: targetTop, behavior });
}

function scheduleMobileProductMediaScroll(scope, delay = 140) {
  if (!isIicaseMobileViewport()) return;

  pendingMobileProductMediaScrollScope = scope instanceof Element ? scope : null;
  window.clearTimeout(pendingMobileProductMediaScrollTimer);

  pendingMobileProductMediaScrollTimer = window.setTimeout(() => {
    const scrollScope = pendingMobileProductMediaScrollScope;
    pendingMobileProductMediaScrollScope = null;
    scrollMobileProductMediaToTop(scrollScope);
  }, delay);
}

function getProductColorSwatchInput(target) {
  if (!(target instanceof Element)) return null;
  if (target.closest('product-card, quick-add-dialog')) return null;

  const fieldset = target.closest('fieldset.variant-option--swatches');
  const picker = target.closest('variant-picker');
  if (!(fieldset instanceof Element) || !(picker instanceof Element)) return null;
  if (fieldset.closest('variant-picker') !== picker) return null;
  if (!picker.closest('main[data-template^="product"]')) return null;

  const input =
    (target instanceof HTMLInputElement && target.matches('input[type="radio"]')
      ? target
      : target.closest('label')?.querySelector('input[type="radio"]')) ||
    fieldset.querySelector('input[type="radio"]');
  const optionName = input?.dataset.optionName || '';

  if (optionName && !/colou?r/i.test(optionName)) return null;

  return input instanceof HTMLInputElement ? input : null;
}

function previewProductColorSwatchChange(target) {
  if (!enableFastProductVariantPreview) return false;

  const input = getProductColorSwatchInput(target);
  if (!input) return false;

  const scope = getIicaseProductScope(input);
  const imageData = getProductVariantPreviewImageData(input);
  if (!imageData?.src) return false;

  if (productVariantPreviewIsStillPending(scope, imageData)) {
    scheduleMobileProductMediaScroll(scope);
    return true;
  }

  pendingProductVariantPreviewImageData = imageData;
  pendingProductVariantPreviewScope = scope;

  queueProductVariantPreview(scope, imageData);
  scheduleMobileProductMediaScroll(scope);
  return true;
}

function getSelectedProductColorSwatchInput(scope) {
  const productScope =
    scope instanceof Element ? getIicaseProductScope(scope) : document.querySelector('main[data-template^="product"]');
  const inputs =
    productScope?.querySelectorAll?.('variant-picker fieldset.variant-option--swatches input[type="radio"]:checked') ||
    document.querySelectorAll('main[data-template^="product"] variant-picker fieldset.variant-option--swatches input[type="radio"]:checked');

  return (
    Array.from(inputs).find((input) => {
      if (!(input instanceof HTMLInputElement)) return false;
      const optionName = input.dataset.optionName || '';
      return !optionName || /colou?r/i.test(optionName);
    }) || null
  );
}

function getProductInformationMedia(scope) {
  const productScope =
    scope instanceof Element ? getIicaseProductScope(scope) : document.querySelector('main[data-template^="product"]');

  return (
    productScope?.querySelector?.('.product-information__media') ||
    document.querySelector('main[data-template^="product"] .product-information__media')
  );
}

function getProductMediaGallery(scope) {
  const productScope =
    scope instanceof Element ? getIicaseProductScope(scope) : document.querySelector('main[data-template^="product"]');

  return (
    productScope?.querySelector?.('.product-information__media media-gallery') ||
    document.querySelector('main[data-template^="product"] .product-information__media media-gallery')
  );
}

function getPrimaryProductMediaImages(gallery) {
  if (!(gallery instanceof Element)) return [];

  const imageSelectors = [
    'slideshow-slides > slideshow-slide:first-of-type img.product-media__image',
    '.media-gallery__grid > li:first-child img.product-media__image',
    '.dialog-zoomed-gallery > li:first-child img.product-media__image',
    '.dialog-thumbnails-list__thumbnail:first-child img',
  ];

  return [...new Set(imageSelectors.flatMap((selector) => Array.from(gallery.querySelectorAll(selector))))].filter(
    (image) => image instanceof HTMLImageElement
  );
}

function getPrimaryProductMediaImage(gallery) {
  return getPrimaryProductMediaImages(gallery)[0] || null;
}

function getProductPhoneFilterMediaMap(scope) {
  const productScope =
    scope instanceof Element ? getIicaseProductScope(scope) : document.querySelector('main[data-template^="product"]');
  const picker =
    productScope?.querySelector?.('variant-picker[data-template-product-match="true"]') ||
    productScope?.querySelector?.('variant-picker') ||
    document.querySelector('main[data-template^="product"] variant-picker');

  return getIicasePhoneFilterMediaMap(picker || productScope, '__iicaseProductPhoneFilterMediaMap');
}

function getProductPhoneFilterPreviewImageData(input) {
  const selectedPhoneColor = getRememberedProductPhoneFilterSelection(input);
  const caseColor = iicaseHandleize(input?.value);
  if (!selectedPhoneColor || !caseColor) return null;

  const phoneFilterEntry = getProductPhoneFilterMediaMap(input).find(
    (entry) => entry.phone === selectedPhoneColor && entry.case === caseColor
  );
  if (!phoneFilterEntry?.src) return null;

  const srcset = phoneFilterEntry.srcset || '';
  const sizes = phoneFilterEntry.sizes || '(min-width: 750px) 50vw, 100vw';

  return {
    alt: phoneFilterEntry.alt || input.getAttribute('aria-label') || input.value,
    fullSrc: phoneFilterEntry.fullSrc || '',
    key: srcset ? `${srcset}|${sizes}` : phoneFilterEntry.src,
    sizes,
    src: phoneFilterEntry.src,
    srcset,
  };
}

function getProductVariantPreviewImageData(input) {
  return getProductPhoneFilterPreviewImageData(input) || getVariantPreloadImageData(input);
}

function getCurrentProductMediaImageData(scope) {
  return getIicaseImageDataFromImage(getPrimaryProductMediaImage(getProductMediaGallery(scope)));
}

function applyProductVariantPreviewToProductSelectHtml(result, imageData, scope = document) {
  const html = result?.detail?.html;
  if (!imageData?.src || !html?.querySelector) return;

  const shouldKeepCurrentImage = productVariantPreviewIsStillPending(scope, imageData);
  const appliedImageData =
    !shouldKeepCurrentImage && isIicaseImageDataLoaded(imageData)
      ? imageData
      : getCurrentProductMediaImageData(scope);
  if (!appliedImageData?.src) return;

  const gallery = html.querySelector('media-gallery');
  getPrimaryProductMediaImages(gallery).forEach((image) => applyIicaseImageData(image, appliedImageData));
}

function productVariantPreviewIsStillPending(scope, imageData) {
  const pendingKey = getIicaseImageDataKey(pendingProductVariantPreviewImageData);
  const nextKey = getIicaseImageDataKey(imageData);
  if (!pendingKey || pendingKey !== nextKey) return false;

  return !pendingProductVariantPreviewScope || pendingProductVariantPreviewScope === scope;
}

function clearPendingProductVariantPreview(scope, imageData) {
  if (!productVariantPreviewIsStillPending(scope, imageData)) return;

  pendingProductVariantPreviewImageData = null;
  pendingProductVariantPreviewScope = null;
}

function clearProductMediaPreviewCover(scope) {
  const media = getProductInformationMedia(scope);
  media?.querySelectorAll?.('.iicase-product-media-preview-cover').forEach((cover) => cover.remove());
}

function hideProductMediaPreviewCover(scope, delay = 160) {
  window.clearTimeout(productMediaPreviewCoverTimer);

  productMediaPreviewCoverTimer = window.setTimeout(() => {
    const media = getProductInformationMedia(scope);
    const covers = media?.querySelectorAll?.('.iicase-product-media-preview-cover') || [];

    covers.forEach((cover) => {
      cover.classList.add('is-fading');
      window.setTimeout(() => cover.remove(), 220);
    });
  }, delay);
}

function showProductMediaPreviewCover(scope) {
  if (!isIicaseMobileViewport()) return null;

  const media = getProductInformationMedia(scope);
  const gallery = getProductMediaGallery(scope);
  const image = getPrimaryProductMediaImage(gallery);
  if (!(media instanceof HTMLElement) || !(image instanceof HTMLImageElement)) return null;

  const mediaRect = media.getBoundingClientRect();
  const imageRect = image.getBoundingClientRect();
  if (mediaRect.width <= 0 || mediaRect.height <= 0 || imageRect.width <= 0 || imageRect.height <= 0) return null;

  clearProductMediaPreviewCover(media);

  const cover = document.createElement('div');
  cover.className = 'iicase-product-media-preview-cover';
  cover.style.left = `${imageRect.left - mediaRect.left}px`;
  cover.style.top = `${imageRect.top - mediaRect.top}px`;
  cover.style.width = `${imageRect.width}px`;
  cover.style.height = `${imageRect.height}px`;

  const clone = image.cloneNode(false);
  clone.removeAttribute('loading');
  clone.removeAttribute('sizes');
  clone.removeAttribute('srcset');
  clone.setAttribute('aria-hidden', 'true');
  clone.decoding = 'async';
  clone.src = image.currentSrc || image.src;

  cover.appendChild(clone);
  media.appendChild(cover);

  window.clearTimeout(productMediaPreviewCoverTimer);
  productMediaPreviewCoverTimer = window.setTimeout(() => hideProductMediaPreviewCover(media, 0), 1800);

  return cover;
}

function applyProductVariantPreviewToLiveGallery(scope, imageData) {
  const gallery = getProductMediaGallery(scope);
  if (!(gallery instanceof Element)) return;

  if (!imageData?.src) return;

  const images = getPrimaryProductMediaImages(gallery);

  images.forEach((image) => {
    applyIicaseImageData(image, imageData);
  });

  gallery.slideshow?.select(0, undefined, { animate: false });
}

function transitionProductVariantPreview(scope, imageData) {
  const applyPreview = () => {
    if (!productVariantPreviewIsStillPending(scope, imageData)) return;

    applyProductVariantPreviewToLiveGallery(scope, imageData);
    hideProductMediaPreviewCover(scope, 30);
    clearPendingProductVariantPreview(scope, imageData);
  };

  if (!isIicaseMobileViewport() || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    applyPreview();
    return;
  }

  const cover = showProductMediaPreviewCover(scope);
  if (!(cover instanceof HTMLElement)) {
    applyPreview();
    return;
  }

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      if (!productVariantPreviewIsStillPending(scope, imageData)) {
        cover.remove();
        return;
      }

      applyPreview();
    });
  });
}

function queueProductVariantPreview(scope, imageData) {
  if (!imageData?.src) return;

  if (isIicaseImageDataLoaded(imageData)) {
    transitionProductVariantPreview(scope, imageData);
    return;
  }

  loadIicaseImageData(imageData, 'high').then((isLoaded) => {
    if (!isLoaded || !productVariantPreviewIsStillPending(scope, imageData)) {
      clearPendingProductVariantPreview(scope, imageData);
      return;
    }

    transitionProductVariantPreview(scope, imageData);
  });
}

function getActiveUpsellOffer(scope) {
  return (
    scope.querySelector(`${upsellOfferSelector}[data-iicase-upsell-active='true']`) ||
    scope.querySelector(upsellOfferSelector)
  );
}

function activateUpsellOffer(root) {
  const scope = getIicaseProductScope(root);

  scope.querySelectorAll(upsellOfferSelector).forEach((candidate) => {
    if (candidate === root) {
      candidate.setAttribute('data-iicase-upsell-active', 'true');
    } else {
      candidate.removeAttribute('data-iicase-upsell-active');
    }
  });
}

function getUpsellProductForm(root) {
  const section = getIicaseProductScope(root);
  const form = section.querySelector('product-form-component');

  return form instanceof HTMLElement ? form : null;
}

function getIicaseProductFormQuantity(productForm) {
  if (!(productForm instanceof HTMLElement)) return 1;

  const quantityInput = productForm.querySelector('input[name="quantity"]');
  const quantity = Number(quantityInput?.value) || Number(productForm.dataset.quantityDefault) || 1;

  return Math.max(1, quantity);
}

function formatIicaseAddToCartMoney(target, cents) {
  const moneyFormat = target.getAttribute('data-iicase-add-to-cart-money-format') || '${{amount}}';
  const currency = target.getAttribute('data-iicase-add-to-cart-currency') || 'AUD';

  return formatMoney(Math.max(0, Math.round(Number(cents) || 0)), moneyFormat, currency);
}

function updateIicaseAddToCartTotalTarget(target, cents) {
  if (!(target instanceof HTMLElement)) return;

  const button = target.closest('button');
  const amount = Number(cents) || 0;

  if (amount <= 0 || button?.disabled) {
    target.hidden = true;
    target.textContent = '';
    return;
  }

  target.hidden = false;
  target.textContent = ` - ${formatIicaseAddToCartMoney(target, amount)}`;
}

function updateIicaseAddToCartTotalsForScope(scope, cents) {
  scope
    .querySelectorAll?.(addToCartTotalSelector)
    .forEach((target) => updateIicaseAddToCartTotalTarget(target, cents));
}

function updateIicaseStandaloneAddToCartTotals(scope = document) {
  scope.querySelectorAll?.(addToCartTotalSelector).forEach((target) => {
    if (!(target instanceof HTMLElement)) return;

    const productScope = getIicaseProductScope(target);
    const offer = productScope.querySelector(upsellOfferSelector);
    if (offer && !offer.closest('iicase-upsell-popup')) return;

    const productForm = target.closest('product-form-component') || productScope.querySelector('product-form-component');
    const variantPrice = Number(target.getAttribute('data-iicase-add-to-cart-variant-price')) || 0;
    const quantity = getIicaseProductFormQuantity(productForm);

    updateIicaseAddToCartTotalTarget(target, variantPrice * quantity);
  });
}

function updateUpsellAddToCartTotal(root, discountedTotal) {
  const productScope = getIicaseProductScope(root);
  const activeRoot = getActiveUpsellOffer(productScope);
  if (activeRoot && activeRoot !== root) return;

  updateIicaseAddToCartTotalsForScope(productScope, discountedTotal);
}

function getCurrentMainUpsellVariant(root, mainProductData) {
  const form = getUpsellProductForm(root);
  const popupVariantId = root.closest('iicase-upsell-popup')?.context?.variantId;
  const variantId = popupVariantId || form?.querySelector('input[name="id"]')?.value || String(mainProductData?.selected_variant_id || '');
  const variants = Array.isArray(mainProductData?.variants) ? mainProductData.variants : [];

  return variants.find((variant) => String(variant.id) === String(variantId)) || variants[0] || null;
}

function getUpsellVariantOptionValue(variant, index) {
  if (!variant || index < 0) return '';

  if (Array.isArray(variant.options)) {
    return variant.options[index] || '';
  }

  return variant[`option${index + 1}`] || '';
}

function getUpsellTargetModel(root, mainProductData, mainVariant, matchNames) {
  const mainOptionIndex = findUpsellMatchedOptionIndex(mainProductData, matchNames);
  const mainOptionValue = getUpsellVariantOptionValue(mainVariant, mainOptionIndex);

  return mainOptionValue || root.getAttribute('data-iicase-upsell-base-model') || '';
}

function getFirstAvailableUpsellVariant(productData) {
  const variants = Array.isArray(productData?.variants) ? productData.variants : [];
  return variants.find((variant) => variant.available) || variants[0] || null;
}

function findMatchingUpsellVariant(root, addonProductData, targetModel, matchNames) {
  const variants = Array.isArray(addonProductData?.variants) ? addonProductData.variants : [];
  if (!variants.length) return null;

  const addonOptionIndex = findUpsellMatchedOptionIndex(addonProductData, matchNames);
  if (addonOptionIndex < 0 || !targetModel) return getFirstAvailableUpsellVariant(addonProductData);

  const normalizedTarget = normalizeUpsellValue(targetModel);
  const exactAvailableVariant = variants.find((variant) => {
    return variant.available && normalizeUpsellValue(getUpsellVariantOptionValue(variant, addonOptionIndex)) === normalizedTarget;
  });

  if (exactAvailableVariant) return exactAvailableVariant;

  return (
    variants.find((variant) => normalizeUpsellValue(getUpsellVariantOptionValue(variant, addonOptionIndex)) === normalizedTarget) ||
    null
  );
}

function normalizeUpsellProductData(productData) {
  if (!productData || typeof productData !== 'object') return null;

  const defaultImage = productData.default_image || productData.image || '';
  const variants = Array.isArray(productData.variants) ? productData.variants : [];

  return {
    ...productData,
    variants: variants.map((variant) => {
      if (!Array.isArray(variant)) {
        return {
          ...variant,
          image: variant.image || defaultImage,
        };
      }

      return {
        id: variant[0],
        available: variant[1] === true || Number(variant[1]) === 1,
        price: Number(variant[2]) || 0,
        compare_at_price: Number(variant[3]) || 0,
        image: variant[4] || defaultImage,
        options: variant.slice(5, 8).map((option) => option || ''),
      };
    }),
  };
}

function getUpsellProductData(root, selector, cacheProperty) {
  const script = root.querySelector(selector);
  const source = script?.textContent?.trim() || '';
  const cached = root[cacheProperty];

  if (cached?.source === source) return cached.data;

  let productData = null;

  if (source) {
    try {
      productData = normalizeUpsellProductData(JSON.parse(source));
    } catch (error) {
      productData = null;
    }
  }

  root[cacheProperty] = { source, data: productData };
  return productData;
}

function getUpsellMainProductData(root) {
  return getUpsellProductData(root, '[data-iicase-upsell-main-product]', '__iicaseUpsellMainProductData');
}

function getUpsellItemData(item) {
  return getUpsellProductData(item, '[data-iicase-upsell-addon-product]', '__iicaseUpsellProductData');
}

function formatUpsellMoney(root, cents) {
  const moneyFormat = root.getAttribute('data-iicase-upsell-money-format') || '${{amount}}';
  const currency = root.getAttribute('data-iicase-upsell-currency') || 'AUD';

  return formatMoney(Math.max(0, Math.round(Number(cents) || 0)), moneyFormat, currency);
}

function setUpsellElementText(element, text) {
  if (!element) return;
  element.textContent = text;
}

function setOptionalUpsellText(element, text) {
  if (!(element instanceof HTMLElement)) return '';

  const value = String(text || '').trim();
  element.textContent = value;
  element.hidden = !value;

  return value;
}

function renderUpsellText(template, values = {}) {
  return Object.entries(values).reduce((text, [key, value]) => {
    return text
      .replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'), String(value ?? ''))
      .replace(new RegExp(`\\[${key}\\]`, 'g'), String(value ?? ''));
  }, String(template || ''));
}

function renderUpsellSavingsText(template, amount) {
  const fallbackTemplate = 'Save {{ amount }}';
  const sourceTemplate = template || fallbackTemplate;
  const renderedText = renderUpsellText(sourceTemplate, {
    amount,
    savings: amount,
  }).trim();

  if (/\{\{\s*(amount|savings)\s*\}\}/.test(sourceTemplate)) {
    return renderedText;
  }

  return `${renderedText || 'Save'} ${amount}`.trim();
}

function buildUpsellVariantUrl(productUrl, variantId) {
  if (!productUrl) return '';

  try {
    const url = new URL(productUrl, window.location.origin);

    if (variantId) {
      url.searchParams.set('variant', String(variantId));
    } else {
      url.searchParams.delete('variant');
    }

    return `${url.pathname}${url.search}${url.hash}`;
  } catch (error) {
    if (!variantId) return productUrl;

    const separator = productUrl.includes('?') ? '&' : '?';
    return `${productUrl}${separator}variant=${encodeURIComponent(String(variantId))}`;
  }
}

function updateUpsellItemProductLinks(item, variant) {
  const variantId = variant?.id || item.querySelector('[data-iicase-upsell-select]')?.value || '';

  item.querySelectorAll('[data-iicase-upsell-product-link]').forEach((link) => {
    if (!(link instanceof HTMLAnchorElement)) return;

    const productUrl = link.getAttribute('data-iicase-upsell-product-url') || link.getAttribute('href') || '';
    const variantUrl = buildUpsellVariantUrl(productUrl, variantId);

    if (variantUrl) link.setAttribute('href', variantUrl);
  });
}

function updateUpsellItemImage(item, variant) {
  if (item.getAttribute('data-iicase-upsell-image-locked') === 'true' || !variant?.image) return;

  const image = item.querySelector('.iicase-upsell-offer__image');
  if (!(image instanceof HTMLImageElement)) return;

  const variantId = String(variant.id || '');
  if (image.dataset.iicaseUpsellVariantImageId === variantId) return;

  image.dataset.iicaseUpsellVariantImageId = variantId;
  image.removeAttribute('srcset');
  image.setAttribute('src', variant.image);
}

function isCompactUpsellItem(item) {
  return item.closest(upsellOfferSelector)?.getAttribute('data-iicase-upsell-layout') === 'compact';
}

function getUpsellItemMinimumQuantity(item) {
  return isCompactUpsellItem(item) ? 0 : 1;
}

function getUpsellItemQuantity(item) {
  const input = item.querySelector('[data-iicase-upsell-quantity]');
  const minimum = getUpsellItemMinimumQuantity(item);
  const fallback = minimum === 0 ? 0 : 1;
  const quantity = input instanceof HTMLInputElement ? Number.parseInt(input.value, 10) : fallback;

  return Math.min(99, Math.max(minimum, Number.isFinite(quantity) ? quantity : fallback));
}

function setUpsellItemQuantity(item, quantity, enabled = true) {
  const input = item.querySelector('[data-iicase-upsell-quantity]');
  const decrease = item.querySelector('[data-iicase-upsell-quantity-action="decrease"]');
  const increase = item.querySelector('[data-iicase-upsell-quantity-action="increase"]');
  const minimum = getUpsellItemMinimumQuantity(item);
  const parsedQuantity = Number.parseInt(quantity, 10);
  const nextQuantity = Math.min(99, Math.max(minimum, Number.isFinite(parsedQuantity) ? parsedQuantity : minimum));

  if (input instanceof HTMLInputElement) {
    input.value = String(nextQuantity);
    input.disabled = !enabled;
  }

  if (decrease instanceof HTMLButtonElement) {
    decrease.disabled = !enabled || nextQuantity <= minimum;
  }

  if (increase instanceof HTMLButtonElement) {
    increase.disabled = !enabled || nextQuantity >= 99;
  }

  return nextQuantity;
}

function updateUpsellItemDisplay(root, item, variant, discountPercent) {
  const select = item.querySelector('[data-iicase-upsell-select]');
  const checkbox = item.querySelector('[data-iicase-upsell-toggle]');
  const status = item.querySelector('[data-iicase-upsell-item-status]');
  const price = item.querySelector('[data-iicase-upsell-item-price]');
  const originalPrice = item.querySelector('[data-iicase-upsell-item-original-price]');

  if (!(select instanceof HTMLSelectElement) || !(checkbox instanceof HTMLInputElement)) return;

  const outOfStockBehavior = root.getAttribute('data-iicase-upsell-oos-behavior') || 'hide';
  const awaitingManualSelection = getUpsellVariantSelectionMode(root) === 'manual-select' && !select.value;

  if (awaitingManualSelection) {
    item.hidden = false;
    item.classList.remove('is-unavailable');
    checkbox.checked = false;
    checkbox.disabled = true;
    select.disabled = false;
    setUpsellItemQuantity(item, 0, false);
    updateUpsellItemProductLinks(item, null);

    if (status) {
      status.hidden = true;
      status.textContent = '';
    }

    return;
  }

  const isAvailable = Boolean(variant?.available);

  if ((!variant || !isAvailable) && outOfStockBehavior === 'hide') {
    item.hidden = true;
    checkbox.checked = false;
    checkbox.disabled = true;
    select.disabled = true;
    if (isCompactUpsellItem(item)) setUpsellItemQuantity(item, 0, false);
    return;
  }

  item.hidden = false;

  if (variant) {
    select.value = String(variant.id);
  }

  updateUpsellItemProductLinks(item, variant);
  updateUpsellItemImage(item, variant);

  checkbox.disabled = !isAvailable;
  select.disabled = !isAvailable;
  const isCompact = isCompactUpsellItem(item);
  const requestedQuantity = isCompact && !isAvailable ? 0 : getUpsellItemQuantity(item);
  const quantity = setUpsellItemQuantity(item, requestedQuantity, isAvailable);
  if (isCompact) checkbox.checked = isAvailable && quantity > 0;
  item.classList.toggle('is-unavailable', !isAvailable);

  if (status) {
    const unavailableText = root.getAttribute('data-iicase-upsell-unavailable-label') || '';

    status.hidden = isAvailable || !unavailableText;
    status.textContent = isAvailable ? '' : unavailableText;
  }

  if (!isAvailable) {
    checkbox.checked = false;
  }

  if (variant && price) {
    const displayQuantity = isCompact ? Math.max(1, quantity) : quantity;
    price.textContent = formatUpsellMoney(root, getUpsellItemDiscountedTotal(variant.price, displayQuantity, discountPercent));
  }

  if (variant && originalPrice) {
    const displayQuantity = isCompact ? Math.max(1, quantity) : quantity;
    const originalValue = discountPercent > 0 ? variant.price * displayQuantity : variant.compare_at_price * displayQuantity;

    originalPrice.hidden = !(discountPercent > 0 || variant.compare_at_price > variant.price);
    originalPrice.textContent = formatUpsellMoney(root, originalValue || variant.price * displayQuantity);
  }
}

function getSelectedUpsellItems(root) {
  return Array.from(root.querySelectorAll(upsellItemSelector)).filter((item) => {
    const checkbox = item.querySelector('[data-iicase-upsell-toggle]');
    const select = item.querySelector('[data-iicase-upsell-select]');

    return (
      !item.hidden &&
      checkbox instanceof HTMLInputElement &&
      checkbox.checked &&
      !checkbox.disabled &&
      select instanceof HTMLSelectElement &&
      !select.disabled &&
      select.value
    );
  });
}

function getSelectedUpsellQuantity(root) {
  return getSelectedUpsellItems(root).reduce((total, item) => total + getUpsellItemQuantity(item), 0);
}

function getUpsellItemDiscountedTotal(price, quantity, discountPercent) {
  const subtotal = (Number(price) || 0) * quantity;
  const percent = Math.min(100, Math.max(0, Number(discountPercent) || 0));
  return Math.round(subtotal * (100 - percent) / 100);
}

function getUpsellDiscountTiers(root) {
  return [
    [1, Number(root.getAttribute('data-iicase-upsell-discount-one')) || 0],
    [2, Number(root.getAttribute('data-iicase-upsell-discount-two')) || 0],
    [3, Number(root.getAttribute('data-iicase-upsell-discount-three')) || 0],
    [4, Number(root.getAttribute('data-iicase-upsell-discount-four')) || 0],
  ];
}

function getUpsellDiscountPercent(root, selectedAddonCount) {
  const tiers = getUpsellDiscountTiers(root);

  return tiers.reduce((bestDiscount, [minimumAddonCount, discountPercent]) => {
    if (selectedAddonCount >= minimumAddonCount && discountPercent > 0) {
      return discountPercent;
    }

    return bestDiscount;
  }, 0);
}

function updateUpsellUnlockedText(root, selectedAddonCount, discountPercent) {
  const message = root.querySelector('[data-iicase-upsell-unlocked]');
  if (!message) return;

  const highestDiscount = Math.max(0, ...getUpsellDiscountTiers(root).map(([, percent]) => percent));
  const unlocked = selectedAddonCount > 0 && highestDiscount > 0 && discountPercent >= highestDiscount;
  const text = unlocked ? renderUpsellText(root.getAttribute('data-iicase-upsell-unlocked-text') || '', {
    percent: discountPercent,
    current_percent: discountPercent,
    selected: selectedAddonCount,
  }) : '';
  setOptionalUpsellText(message, text);

  const copy = root.querySelector('[data-iicase-upsell-copy]');
  if (copy instanceof HTMLElement) {
    copy.hidden = copy.getAttribute('data-has-static-copy') !== 'true' && !text.trim();
  }
}

function renderUpsellGiftText(root, title, value, unlocked, count = 0) {
  const template = root.getAttribute(unlocked
    ? 'data-iicase-upsell-free-gift-unlocked-label'
    : 'data-iicase-upsell-free-gift-locked-label') || '';
  const values = {
    count,
    gift_title: title,
    gift_value: formatUpsellMoney(root, value),
    value: formatUpsellMoney(root, value),
  };
  let text = renderUpsellText(template, values);
  const valueTemplate = root.getAttribute('data-iicase-upsell-free-gift-value-label') || '';
  // A value already embedded in the message must not be appended a second time.
  const hasInlineValue = /\[\s*(?:gift_value|value)\s*\]|\{\{\s*(?:gift_value|value)\s*\}\}/.test(template);
  if (text.trim() && unlocked && value > 0 && valueTemplate && !hasInlineValue) {
    const valueText = renderUpsellText(valueTemplate, values).trim();
    if (valueText) text += ` (${valueText})`;
  }
  return text;
}

function getUpsellDiscountCode(root, selectedAddonCount) {
  if (selectedAddonCount <= 0 || getUpsellDiscountPercent(root, selectedAddonCount) <= 0) return '';

  return String(root.getAttribute('data-iicase-upsell-discount-code') || '').trim();
}

function getUnlockedUpsellGift(root, addonQuantity, giftAlreadyInCart = false) {
  const freeGift = root.querySelector('[data-iicase-upsell-free-gift]');
  const freeGiftMinItems = Number(root.getAttribute('data-iicase-upsell-free-gift-min-items')) || 0;

  if (!freeGift || (!giftAlreadyInCart && (freeGiftMinItems <= 0 || addonQuantity < freeGiftMinItems))) {
    return null;
  }

  const variantId = freeGift.getAttribute('data-gift-variant-id') || '';
  const available = freeGift.getAttribute('data-gift-available') !== 'false';
  const price = Number(freeGift.getAttribute('data-gift-price')) || 0;

  if (!variantId || (!giftAlreadyInCart && (!available || price > 0))) {
    return null;
  }

  return {
    element: freeGift,
    title: freeGift.getAttribute('data-gift-title') || 'gift',
    value: Number(freeGift.getAttribute('data-gift-value')) || 0,
    variantId,
  };
}

function updateIicasePurchaseSummaries(
  root,
  {
    discountedTotal,
    originalTotalValue,
    savings,
    selectedAddonQuantity,
    unlockedGift,
  }
) {
  const section = root.closest('.shopify-section') || getIicaseProductScope(root);
  const formattedSavings = formatUpsellMoney(root, savings);

  section.querySelectorAll('[data-iicase-purchase-summary]').forEach((summary) => {
    if (!(summary instanceof HTMLElement)) return;

    // Keep the current purchase price visible even before an accessory is selected.
    summary.hidden = false;
    const savingSummary = summary.querySelector('[data-iicase-purchase-saving]');
    if (savingSummary instanceof HTMLElement) savingSummary.dataset.hasSavings = String(savings > 0);
    setOptionalUpsellText(
      summary.querySelector('[data-iicase-purchase-saving-label]'),
      'Saving:'
    );
    setOptionalUpsellText(summary.querySelector('[data-iicase-purchase-saving-value]'), formattedSavings);
    setUpsellSummaryBenefit(
      summary.querySelector('[data-iicase-purchase-gift]'),
      unlockedGift ? renderUpsellGiftText(root, unlockedGift.title, unlockedGift.value, true) : ''
    );
    setUpsellElementText(
      summary.querySelector('[data-iicase-purchase-current-total]'),
      formatUpsellMoney(root, discountedTotal)
    );

    const originalTotal = summary.querySelector('[data-iicase-purchase-original-total]');
    setUpsellElementText(originalTotal, formatUpsellMoney(root, originalTotalValue));
    if (originalTotal instanceof HTMLElement) originalTotal.hidden = savings <= 0;
  });

  const productForm = getUpsellProductForm(root);
  const addToCartButton = productForm?.querySelector('[ref="addToCartButton"]');
  const buyNowText = 'Buy Now';

  section.querySelectorAll('[data-iicase-buy-now]').forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;

    const label = setOptionalUpsellText(button.querySelector('[data-iicase-buy-now-label]'), buyNowText);
    button.hidden = !label;
    button.disabled = !(addToCartButton instanceof HTMLButtonElement) || addToCartButton.disabled;
  });
}

function setUpsellSummaryBenefit(element, value) {
  if (!(element instanceof HTMLElement)) return '';

  const text = String(value || '').trim();
  const leadingSeparator = text.match(/^([+·])\s*/);
  const label = element.querySelector('[data-iicase-purchase-benefit-label]');
  const separator = element.querySelector('[data-iicase-purchase-benefit-separator]');

  element.hidden = !text;
  if (separator instanceof HTMLElement) {
    separator.hidden = !leadingSeparator;
    if (leadingSeparator) separator.textContent = leadingSeparator[1];
  }
  if (label instanceof HTMLElement) {
    label.textContent = leadingSeparator ? text.slice(leadingSeparator[0].length) : text;
  }

  return text;
}

function updateUpsellTierProgress(root, selectedAddonCount) {
  const progress = root.querySelector('[data-iicase-upsell-tier-progress]');
  const progressFill = root.querySelector('[data-iicase-upsell-tier-progress-fill]');
  const maxAddons = Number(root.getAttribute('data-iicase-upsell-rewards-max-addons')) || 0;
  if (!progress || maxAddons <= 0) return;

  const currentAddons = Math.min(Math.max(0, selectedAddonCount), maxAddons);
  const progressPercent = (currentAddons / maxAddons) * 100;
  const segmentPercent = 100 / maxAddons;

  if (progressFill instanceof HTMLElement) {
    progressFill.style.width = `${Math.min(100, progressPercent)}%`;
  }

  progress.style.setProperty('--iicase-upsell-progress-start', `${progressPercent}%`);
  progress.style.setProperty('--iicase-upsell-progress-segment', `${segmentPercent}%`);
  progress.classList.toggle('is-complete', currentAddons >= maxAddons);
  progress.setAttribute('aria-valuenow', String(currentAddons));
  progress.querySelectorAll('[data-iicase-upsell-tier]').forEach((milestone) => {
    const tier = Number(milestone.getAttribute('data-iicase-upsell-tier')) || 0;
    milestone.classList.toggle('is-reached', currentAddons >= tier);
    milestone.classList.toggle('is-current', currentAddons === tier);
  });
}

function updateUpsellGift(root, addonQuantity, giftAlreadyInCart = false) {
  const freeGift = root.querySelector('[data-iicase-upsell-free-gift]');
  const freeGiftLabel = root.querySelector('[data-iicase-upsell-free-gift-label]');
  const freeGiftMinItems = Number(root.getAttribute('data-iicase-upsell-free-gift-min-items')) || 0;

  if (freeGift && freeGiftLabel) {
    const giftVariantId = freeGift.getAttribute('data-gift-variant-id') || '';
    const giftPrice = Number(freeGift.getAttribute('data-gift-price')) || 0;
    const giftAvailable =
      giftAlreadyInCart || (Boolean(giftVariantId) && freeGift.getAttribute('data-gift-available') !== 'false' && giftPrice <= 0);
    const giftTitle = freeGift.getAttribute('data-gift-title') || '';
    const giftValue = Number(freeGift.getAttribute('data-gift-value')) || 0;

    freeGift.hidden = !giftAvailable;
    if (!giftAvailable) return;

    const unlocked = giftAlreadyInCart || (giftAvailable && freeGiftMinItems > 0 && addonQuantity >= freeGiftMinItems);
    const lockedCount = giftAlreadyInCart ? 0 : Math.max(0, freeGiftMinItems - addonQuantity);
    freeGift.classList.toggle('is-unlocked', unlocked);
    freeGift.classList.toggle('is-unavailable', !giftAvailable);
    const giftText = renderUpsellGiftText(root, giftTitle, giftValue, unlocked, lockedCount);

    setOptionalUpsellText(freeGiftLabel, giftText);
    freeGift.hidden = !giftText.trim();
  }
}

function updateUpsellItemsDisclosure(root) {
  const popup = root.closest('iicase-upsell-popup');
  if (popup) {
    root.querySelectorAll(upsellItemSelector).forEach((item) => item.removeAttribute('data-iicase-upsell-extra-item'));
    return;
  }
  const visibleLimit = Number(root.getAttribute('data-iicase-upsell-mobile-visible-items')) || 3;
  const items = Array.from(root.querySelectorAll(upsellItemSelector));
  let availablePosition = 0;

  items.forEach((item) => {
    item.removeAttribute('data-iicase-upsell-extra-item');
    if (item.hidden) return;

    availablePosition += 1;
    if (availablePosition > visibleLimit) {
      item.setAttribute('data-iicase-upsell-extra-item', '');
    }
  });

  const toggle = root.querySelector('[data-iicase-upsell-items-toggle]');
  if (!(toggle instanceof HTMLButtonElement)) return;

  const hasExtraItems = availablePosition > visibleLimit;
  toggle.hidden = !hasExtraItems;

  if (!hasExtraItems) {
    root.setAttribute('data-iicase-upsell-items-expanded', 'false');
    toggle.setAttribute('aria-expanded', 'false');
  }
}

function refreshUpsellOffer(root) {
  const mainProductData = getUpsellMainProductData(root);
  if (!mainProductData) return;

  const mainVariant = getCurrentMainUpsellVariant(root, mainProductData);
  if (!mainVariant) return;

  const matchNames = getUpsellMatchNames(root);
  const targetModel = getUpsellTargetModel(root, mainProductData, mainVariant, matchNames);
  const selectionMode = getUpsellVariantSelectionMode(root);

  root.querySelectorAll(upsellItemSelector).forEach((item) => {
    const addonProductData = getUpsellItemData(item);
    const select = item.querySelector('[data-iicase-upsell-select]');
    if (!addonProductData || !(select instanceof HTMLSelectElement)) return;

    let variant = null;

    if (selectionMode === 'manual-select' || item.getAttribute('data-iicase-upsell-manual') === 'true') {
      variant = addonProductData.variants?.find((candidate) => String(candidate.id) === select.value) || null;
    } else {
      variant = findMatchingUpsellVariant(root, addonProductData, targetModel, matchNames);
    }

    updateUpsellItemDisplay(root, item, variant, 0);
  });

  updateUpsellItemsDisclosure(root);
  updateUpsellSummary(root);
}

function updateUpsellSummary(root) {
  const mainProductData = getUpsellMainProductData(root);
  const mainVariant = getCurrentMainUpsellVariant(root, mainProductData);
  if (!mainVariant) return;

  const selectedItems = getSelectedUpsellItems(root);
  const selectedAddonQuantity = getSelectedUpsellQuantity(root);
  const cartState = getCurrentUpsellCartState(root);
  const rewardAddonQuantity = cartState.addonQuantity + selectedAddonQuantity;
  const discountPercent = getUpsellDiscountPercent(root, rewardAddonQuantity);
  const mainQuantity = getIicaseProductFormQuantity(getUpsellProductForm(root));
  const mainSubtotal = (Number(mainVariant.price) || 0) * mainQuantity;
  let addonSubtotal = 0;
  let discountedAddonSubtotal = 0;

  selectedItems.forEach((item) => {
    const addonProductData = getUpsellItemData(item);
    const select = item.querySelector('[data-iicase-upsell-select]');
    const variant = addonProductData?.variants?.find((candidate) => String(candidate.id) === select?.value);

    if (variant) {
      const quantity = getUpsellItemQuantity(item);
      addonSubtotal += (Number(variant.price) || 0) * quantity;
      discountedAddonSubtotal += getUpsellItemDiscountedTotal(variant.price, quantity, discountPercent);
    }
  });

  const unlockedGift = getUnlockedUpsellGift(root, rewardAddonQuantity, cartState.giftQuantity > 0);
  const discountedTotal = mainSubtotal + discountedAddonSubtotal;
  const savings = addonSubtotal - discountedAddonSubtotal;
  const originalTotalValue = mainSubtotal + addonSubtotal;

  const popup = root.closest('iicase-upsell-popup');
  if (popup) {
    const section = root.closest('.shopify-section') || getIicaseProductScope(root);
    section.querySelectorAll('[data-iicase-purchase-summary]').forEach((summary) => { summary.hidden = true; });
  }
  if (!popup) updateIicasePurchaseSummaries(root, {
    discountedTotal,
    originalTotalValue,
    savings,
    selectedAddonQuantity,
    unlockedGift,
  });

  updateUpsellUnlockedText(root, rewardAddonQuantity, discountPercent);

  root.querySelectorAll(upsellItemSelector).forEach((item) => {
    const addonProductData = getUpsellItemData(item);
    const select = item.querySelector('[data-iicase-upsell-select]');
    const variant = addonProductData?.variants?.find((candidate) => String(candidate.id) === select?.value) || null;
    updateUpsellItemDisplay(root, item, variant, discountPercent);
  });

  updateUpsellTierProgress(root, rewardAddonQuantity);
  updateUpsellGift(root, rewardAddonQuantity, cartState.giftQuantity > 0);
  updateUpsellAddToCartTotal(root, popup ? mainSubtotal : discountedTotal);
  popup?.updateSelection?.(selectedAddonQuantity, formatUpsellMoney(root, discountedAddonSubtotal));
}

document.addEventListener('iicase:upsell-popup-refresh', (event) => {
  initializeUpsellOffers(event.target);
});

function initializeUpsellOffers(scope = document) {
  const roots = Array.from(scope.querySelectorAll?.(upsellOfferSelector) || []);
  const hasActiveRoot = roots.some((root) => root.getAttribute('data-iicase-upsell-active') === 'true');

  if (!hasActiveRoot && roots[0]) activateUpsellOffer(roots[0]);
  roots.forEach((root) => refreshUpsellOffer(root));
  updateIicaseStandaloneAddToCartTotals(scope);
}

function scheduleUpsellOfferRefresh(scope = document, delay = 0) {
  window.setTimeout(() => initializeUpsellOffers(scope), delay);
}

document.addEventListener('change', (event) => {
  if (!(event.target instanceof Element)) return;

  const root = event.target.closest(upsellOfferSelector);
  if (!root) return;
  activateUpsellOffer(root);

  if (event.target.matches('[data-iicase-upsell-select]')) {
    const item = event.target.closest(upsellItemSelector);
    const addonProductData = item ? getUpsellItemData(item) : null;
    const selectedVariant = addonProductData?.variants?.find(
      (candidate) => String(candidate.id) === event.target.value
    );
    const checkbox = item?.querySelector('[data-iicase-upsell-toggle]');

    item?.setAttribute('data-iicase-upsell-manual', 'true');

    if (item && selectedVariant?.available && !root.closest('iicase-upsell-popup')) {
      const currentQuantity = getUpsellItemQuantity(item);
      const nextQuantity = currentQuantity === 0 ? setUpsellItemQuantity(item, 1, true) : currentQuantity;

      if (checkbox instanceof HTMLInputElement) {
        checkbox.disabled = false;
        checkbox.checked = isCompactUpsellItem(item) ? nextQuantity > 0 : true;
      }
    }
  }

  if (event.target.matches('[data-iicase-upsell-toggle]') && event.target.checked) {
    event.target.closest(upsellItemSelector)?.setAttribute('data-iicase-upsell-manual', 'true');
  }

  if (event.target.matches('[data-iicase-upsell-quantity]')) {
    const item = event.target.closest(upsellItemSelector);
    const checkbox = item?.querySelector('[data-iicase-upsell-toggle]');

    const quantity = item ? setUpsellItemQuantity(item, event.target.value, true) : 0;
    if (checkbox instanceof HTMLInputElement && !checkbox.disabled) {
      checkbox.checked = isCompactUpsellItem(item) ? quantity > 0 : true;
    }
    if (quantity > 0) item?.setAttribute('data-iicase-upsell-manual', 'true');
  }

  updateUpsellSummary(root);
});

document.addEventListener('shopify:cart:lines-update', async (event) => {
  const productForm = event.target;
  if (!(productForm instanceof HTMLElement) || !productForm.matches('product-form-component')) return;
  if (event.action !== 'add' || !event.promise || productForm.dataset.iicaseDirectCheckout === 'true') return;

  const section = productForm.closest('.shopify-section');
  if (!section) return;
  const buttonSelector = 'product-form-component .add-to-cart-button, sticky-add-to-cart .add-to-cart-button';
  const cartItems = Array.from(document.querySelectorAll('cart-items-component'));
  section.dataset.iicaseCartLoading = 'true';
  section.querySelectorAll(buttonSelector).forEach((button) => button.setAttribute('aria-busy', 'true'));
  let succeeded = false;

  try {
    const result = await event.promise;
    if (result?.detail?.didError) return;
    await Promise.all(cartItems.map((items) => items.cartUpdateComplete));
    succeeded = true;
  } catch (error) {
    if (error?.name !== 'AbortError') console.warn('[iicase-cart] Add to cart failed:', error);
  } finally {
    delete section.dataset.iicaseCartLoading;
    // Re-query because a variant update can replace the original button nodes.
    section.querySelectorAll(buttonSelector).forEach((button) => {
      button.removeAttribute('aria-busy');
      button.removeAttribute('data-added');
      if (!succeeded) return;

      requestAnimationFrame(() => {
        if (!button.isConnected) return;
        const component = button.closest('add-to-cart-component');
        if (typeof component?.animateAddToCart === 'function') {
          component.animateAddToCart();
        } else {
          button.dataset.added = 'true';
          window.setTimeout(() => button.removeAttribute('data-added'), 1800);
        }
      });
    });
  }
});

function handleIicaseBuyNow(button) {
  const section = button.closest('.shopify-section');
  if (section?.dataset.iicaseCartLoading === 'true') return;
  const productForm = section?.querySelector('product-form-component');
  const addToCartButton = productForm?.querySelector('[ref="addToCartButton"]');

  if (!(productForm instanceof HTMLElement) || !(addToCartButton instanceof HTMLButtonElement)) return;
  if (addToCartButton.disabled || button.disabled) return;

  const buyNowButtons = Array.from(section.querySelectorAll('[data-iicase-buy-now]')).filter(
    (candidate) => candidate instanceof HTMLButtonElement
  );
  let timeoutId = 0;

  const cleanup = () => {
    document.removeEventListener('shopify:cart:lines-update', handleCartUpdate, true);
    document.removeEventListener('shopify:cart:error', handleCartError, true);
    window.clearTimeout(timeoutId);
    productForm.removeAttribute('data-iicase-direct-checkout');
  };

  const reset = () => {
    cleanup();
    buyNowButtons.forEach((candidate) => {
      candidate.removeAttribute('aria-busy');
      candidate.disabled = addToCartButton.disabled;
    });
  };

  const handleCartUpdate = (cartEvent) => {
    if (cartEvent.target !== productForm || typeof cartEvent.promise?.then !== 'function') return;

    cartEvent.promise
      .then((result) => {
        if (result?.detail?.didError) {
          reset();
          return;
        }

        cleanup();
        const rootPath = window.Shopify?.routes?.root || '/';
        window.location.assign(`${rootPath}checkout`);
      })
      .catch(reset);
  };

  const handleCartError = (cartEvent) => {
    if (cartEvent.target === productForm) reset();
  };

  buyNowButtons.forEach((candidate) => {
    candidate.setAttribute('aria-busy', 'true');
    candidate.disabled = true;
  });
  document.addEventListener('shopify:cart:lines-update', handleCartUpdate, true);
  document.addEventListener('shopify:cart:error', handleCartError, true);
  timeoutId = window.setTimeout(reset, 20000);
  productForm.dataset.iicaseDirectCheckout = 'true';
  addToCartButton.click();
}

window.addEventListener('pageshow', () => {
  document.querySelectorAll('[data-iicase-cart-loading]').forEach((section) => {
    delete section.dataset.iicaseCartLoading;
    section.querySelectorAll('.add-to-cart-button[aria-busy]').forEach((button) => button.removeAttribute('aria-busy'));
  });
  document.querySelectorAll('[data-iicase-buy-now][aria-busy="true"]').forEach((button) => {
    button.removeAttribute('aria-busy');
  });
  document.querySelectorAll('product-form-component[data-iicase-direct-checkout]').forEach((productForm) => {
    productForm.removeAttribute('data-iicase-direct-checkout');
  });
  scheduleUpsellOfferRefresh();
});

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const buyNowButton = event.target.closest('[data-iicase-buy-now]');
  if (buyNowButton instanceof HTMLButtonElement) {
    event.preventDefault();
    handleIicaseBuyNow(buyNowButton);
    return;
  }

  const itemAction = event.target.closest('[data-iicase-upsell-item-action]');
  if (itemAction instanceof HTMLButtonElement) {
    const item = itemAction.closest(upsellItemSelector);
    const root = itemAction.closest(upsellOfferSelector);
    const checkbox = item?.querySelector('[data-iicase-upsell-toggle]');
    if (itemAction.disabled || !item || !root?.closest('iicase-upsell-popup') || !(checkbox instanceof HTMLInputElement) || checkbox.disabled) return;
    activateUpsellOffer(root);
    const selected = !checkbox.checked;
    setUpsellItemQuantity(item, selected ? 1 : 0, true);
    checkbox.checked = selected;
    item.setAttribute('data-iicase-upsell-manual', 'true');
    updateUpsellSummary(root);
    return;
  }

  const quantityAction = event.target.closest('[data-iicase-upsell-quantity-action]');
  if (quantityAction instanceof HTMLButtonElement) {
    const item = quantityAction.closest(upsellItemSelector);
    const root = quantityAction.closest(upsellOfferSelector);
    const checkbox = item?.querySelector('[data-iicase-upsell-toggle]');
    if (!item || !root) return;
    activateUpsellOffer(root);

    const direction = quantityAction.getAttribute('data-iicase-upsell-quantity-action') === 'increase' ? 1 : -1;
    const quantity = setUpsellItemQuantity(item, getUpsellItemQuantity(item) + direction, true);
    if (checkbox instanceof HTMLInputElement && !checkbox.disabled) {
      checkbox.checked = isCompactUpsellItem(item) ? quantity > 0 : true;
    }
    if (quantity > 0) item.setAttribute('data-iicase-upsell-manual', 'true');
    updateUpsellSummary(root);
    return;
  }

  const toggle = event.target.closest('[data-iicase-upsell-items-toggle]');
  if (!(toggle instanceof HTMLButtonElement)) return;

  const root = toggle.closest(upsellOfferSelector);
  if (!(root instanceof HTMLElement)) return;
  activateUpsellOffer(root);

  const expanded = root.getAttribute('data-iicase-upsell-items-expanded') === 'true';
  const nextExpanded = !expanded;

  root.setAttribute('data-iicase-upsell-items-expanded', String(nextExpanded));
  toggle.setAttribute('aria-expanded', String(nextExpanded));

  const moreLabel = toggle.querySelector('[data-iicase-upsell-items-toggle-more]');
  const lessLabel = toggle.querySelector('[data-iicase-upsell-items-toggle-less]');
  if (moreLabel instanceof HTMLElement) moreLabel.hidden = nextExpanded;
  if (lessLabel instanceof HTMLElement) lessLabel.hidden = !nextExpanded;
});

function refreshIicaseAddToCartTotalsFromElement(element) {
  const productForm = element.closest('product-form-component');
  if (!productForm?.querySelector(addToCartTotalSelector)) return;

  const scope = getIicaseProductScope(productForm);
  const root = getActiveUpsellOffer(scope);

  if (root) {
    updateUpsellSummary(root);
    return;
  }

  updateIicaseStandaloneAddToCartTotals(scope);
}

document.addEventListener('quantity-selector:update', (event) => {
  if (event.target instanceof Element) {
    refreshIicaseAddToCartTotalsFromElement(event.target);
  }
});

document.addEventListener('input', (event) => {
  if (!(event.target instanceof HTMLInputElement)) return;

  if (event.target.matches('[data-iicase-upsell-quantity]')) {
    const quantity = Number.parseInt(event.target.value, 10);

    const item = event.target.closest(upsellItemSelector);
    const root = event.target.closest(upsellOfferSelector);
    const checkbox = item?.querySelector('[data-iicase-upsell-toggle]');
    if (!item || !root) return;
    if (!Number.isFinite(quantity) || quantity < getUpsellItemMinimumQuantity(item)) return;
    activateUpsellOffer(root);

    const nextQuantity = setUpsellItemQuantity(item, quantity, true);
    if (checkbox instanceof HTMLInputElement && !checkbox.disabled) {
      checkbox.checked = isCompactUpsellItem(item) ? nextQuantity > 0 : true;
    }
    if (nextQuantity > 0) item.setAttribute('data-iicase-upsell-manual', 'true');
    updateUpsellSummary(root);
    return;
  }

  if (event.target.matches('input[name="quantity"]')) {
    refreshIicaseAddToCartTotalsFromElement(event.target);
  }
});

document.addEventListener('change', (event) => {
  if (event.target instanceof HTMLInputElement && event.target.matches('input[name="quantity"]')) {
    refreshIicaseAddToCartTotalsFromElement(event.target);
  }
});

window.iicaseBuildUpsellCartPayload = function iicaseBuildUpsellCartPayload(productForm, formData, options = {}) {
  if (!(productForm instanceof HTMLElement)) return null;

  const section = getIicaseProductScope(productForm);
  const root = getActiveUpsellOffer(section);
  if (!root) return null;

  const popup = root.closest('iicase-upsell-popup');
  if (popup && !options.popupAddons) return popup.prepare?.(productForm, formData, root) || null;

  updateUpsellSummary(root);

  const selectedItems = getSelectedUpsellItems(root);
  const selectedAddonQuantity = getSelectedUpsellQuantity(root);
  const cartState = getCurrentUpsellCartState(root);
  const bundleItemCount = selectedAddonQuantity + (Number(formData.get('quantity')) || 1);
  const rewardAddonQuantity = cartState.addonQuantity + selectedAddonQuantity;
  const unlockedGift = cartState.giftQuantity > 0 ? null : getUnlockedUpsellGift(root, rewardAddonQuantity);
  if (!selectedItems.length && !unlockedGift && !cartState.addonQuantity) return null;

  const discountPercent = getUpsellDiscountPercent(root, rewardAddonQuantity);
  const discountCode = getUpsellDiscountCode(root, rewardAddonQuantity);
  const freeGiftMinItems = Number(root.getAttribute('data-iicase-upsell-free-gift-min-items')) || 0;
  const groupId = options.groupId || `iicase-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const offerId = root.getAttribute('data-iicase-upsell-offer-id') || '';
  const parentVariantId = String(formData.get('id') || '');
  const commonProperties = {
    _iicase_upsell_group: groupId,
    _iicase_upsell_offer: offerId,
    _iicase_upsell_addon_count: String(selectedAddonQuantity),
    _iicase_upsell_item_count: String(bundleItemCount),
    _iicase_upsell_discount_percent: String(discountPercent),
  };

  const itemTags = normalizeUpsellTags(root.getAttribute('data-iicase-upsell-inner-product-tags'));
  if (itemTags.length) commonProperties._iicase_upsell_item_tags = JSON.stringify(itemTags);

  if (discountCode) {
    commonProperties._iicase_upsell_discount_code = discountCode;
  }

  if (freeGiftMinItems > 0) {
    commonProperties._iicase_upsell_free_gift_min_items = String(freeGiftMinItems);
  }

  const addonItems = selectedItems
    .map((item) => {
      const select = item.querySelector('[data-iicase-upsell-select]');
      const addonProductData = getUpsellItemData(item);
      const variant = addonProductData?.variants?.find((candidate) => String(candidate.id) === select?.value);

      if (!variant?.available) return null;

      return {
        variantId: String(variant.id),
        quantity: getUpsellItemQuantity(item),
        properties: {
          ...commonProperties,
          _iicase_upsell_role: 'addon',
          _iicase_upsell_parent: parentVariantId,
        },
      };
    })
    .filter(Boolean);

  const giftItem = unlockedGift
    ? {
        variantId: unlockedGift.variantId,
        quantity: 1,
        properties: {
          ...commonProperties,
          _iicase_upsell_role: 'gift',
          _iicase_upsell_parent: parentVariantId,
          _iicase_upsell_discount_percent: '0',
          _iicase_upsell_gift_price_mode: 'free-product',
          _iicase_upsell_gift_value: String(unlockedGift.value),
        },
      }
    : null;

  upsellPendingSelections.set(productForm, {
    offerId,
    items: addonItems.map(({ variantId, quantity }) => ({ variantId, quantity })),
  });

  return {
    mainProperties: {
      ...commonProperties,
      _iicase_upsell_role: 'main',
      _iicase_upsell_discount_percent: '0',
    },
    items: giftItem ? [...addonItems, giftItem] : addonItems,
    discountCode,
  };
};

window.iicaseBuildUpsellPopupPayload = async function iicaseBuildUpsellPopupPayload(popup) {
  await refreshUpsellCartState();
  const context = popup.context;
  if (!context || !popup.isConnected) return null;
  const formData = new FormData();
  formData.set('id', context.variantId);
  formData.set('quantity', String(context.quantity));
  activateUpsellOffer(context.root);
  return window.iicaseBuildUpsellCartPayload(context.form, formData, { popupAddons: true, groupId: context.groupId });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeUpsellOffers(), { once: true });
} else {
  initializeUpsellOffers();
}

function getVariantPreloadImageData(input) {
  const src = isIicaseMobileViewport()
    ? input.getAttribute('data-iicase-preload-image-mobile') || input.getAttribute('data-iicase-preload-image')
    : input.getAttribute('data-iicase-preload-image');
  const srcset = input.getAttribute('data-iicase-preload-image-srcset') || '';
  const sizes = isIicaseMobileViewport() ? '100vw' : '50vw';

  return {
    alt: input.getAttribute('data-iicase-preload-image-alt') || input.getAttribute('aria-label') || input.value,
    fullSrc: input.getAttribute('data-iicase-preload-image-full') || '',
    key: srcset ? `${srcset}|${sizes}` : src,
    sizes,
    src,
    srcset,
  };
}

function preloadVariantImages() {
  const imageInputs = document.querySelectorAll(
    'main[data-template^="product"] variant-picker [data-iicase-preload-image]'
  );

  for (const input of imageInputs) {
    if (input instanceof HTMLInputElement && input.checked) continue;

    const imageData = getProductVariantPreviewImageData(input);
    if (!imageData?.src) continue;

    loadIicaseImageData(imageData, 'low');
  }
}

function canAutoPreloadVariantImages() {
  return Boolean(document.querySelector('main[data-template^="product"] variant-picker [data-iicase-preload-image]'));
}

function scheduleVariantImagePreload(delay = 0, { force = false } = {}) {
  if (!force && !canAutoPreloadVariantImages()) return;

  window.setTimeout(() => {
    if (!force && 'requestIdleCallback' in window) {
      window.requestIdleCallback(preloadVariantImages, { timeout: 3000 });
      return;
    }

    preloadVariantImages();
  }, delay);
}

function bindVariantImagePreloadOnInteraction() {
  const picker = document.querySelector('main[data-template^="product"] variant-picker');
  if (!picker) return;

  const preloadOnInteraction = () => scheduleVariantImagePreload(0, { force: true });

  picker.addEventListener('pointerenter', preloadOnInteraction, { once: true, passive: true });
  picker.addEventListener('pointerdown', preloadOnInteraction, { once: true, passive: true });
  picker.addEventListener('focusin', preloadOnInteraction, { once: true });
}

function bindVariantImagePreloadNearPicker() {
  const picker = document.querySelector('main[data-template^="product"] variant-picker');
  if (!picker) return;

  if (!('IntersectionObserver' in window)) {
    if (!isIicaseMobileViewport()) scheduleVariantImagePreload(2000);
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;

      observer.disconnect();
      scheduleVariantImagePreload(0, { force: isIicaseMobileViewport() });
    },
    { rootMargin: isIicaseMobileViewport() ? '120px 0px' : '240px 0px', threshold: 0 }
  );

  observer.observe(picker);
}

function initializeVariantImagePreloading() {
  if (!enableFastProductVariantPreview) return;

  bindVariantImagePreloadOnInteraction();

  const bindNearPicker = () => window.setTimeout(bindVariantImagePreloadNearPicker, 400);
  if (document.readyState === 'complete') {
    bindNearPicker();
  } else {
    window.addEventListener('load', bindNearPicker, { once: true });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeVariantImagePreloading, { once: true });
} else {
  initializeVariantImagePreloading();
}

document.addEventListener('shopify:product:select', (event) => {
  const scope = event.target instanceof Element ? getIicaseProductScope(event.target) : document;
  const clearVariantLoading = showProductVariantLoading(event);
  const selectedPhoneColor = getRememberedProductPhoneFilterSelection(scope);
  const shouldScrollMobileProductMedia = pendingMobileProductMediaScrollScope && isIicaseMobileViewport();
  const selectedColorInput = enableFastProductVariantPreview ? getSelectedProductColorSwatchInput(scope) : null;
  const selectedColorOptionValueId = selectedColorInput?.dataset.optionValueId || '';
  const changedOptionValueId = String(event.detail?.optionValueId || '');
  const selectedColorWasChanged =
    Boolean(changedOptionValueId) && changedOptionValueId === selectedColorOptionValueId;
  const pendingPreviewImageData =
    pendingProductVariantPreviewScope === scope ? pendingProductVariantPreviewImageData : null;
  const previewImageData = selectedColorWasChanged
    ? pendingPreviewImageData || (selectedColorInput && getProductVariantPreviewImageData(selectedColorInput))
    : null;

  if (!selectedColorWasChanged && pendingPreviewImageData) {
    clearPendingProductVariantPreview(scope, pendingPreviewImageData);
    hideProductMediaPreviewCover(scope, 0);
  }

  if (selectedColorInput && previewImageData && !productVariantPreviewIsStillPending(scope, previewImageData)) {
    pendingProductVariantPreviewImageData = previewImageData;
    pendingProductVariantPreviewScope = scope;
    queueProductVariantPreview(scope, previewImageData);
  }

  event.promise
    ?.then((result) => {
      applyPhoneFilterToProductSelectHtml(result, selectedPhoneColor);
      applyProductVariantPreviewToProductSelectHtml(result, previewImageData, scope);
      scheduleVariantImagePreload(300);
      scheduleUpsellOfferRefresh(scope, 50);

      if (shouldScrollMobileProductMedia) {
        scheduleMobileProductMediaScroll(scope, 40);
      }

      if (selectedPhoneColor) {
        scheduleProductPhoneFilterRestore(scope, selectedPhoneColor);
        scheduleProductPhoneFilterRestore(scope, selectedPhoneColor, 150);
      }

    })
    .catch(() => {
      hideProductMediaPreviewCover(scope, 80);
      clearPendingProductVariantPreview(scope, previewImageData);
    })
    .finally(() => clearVariantLoading?.());
}, true);
